import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import React from 'react';
import { 
  getCachedTasks, 
  setCachedTasks, 
  clearTaskCache, 
  updateCachedTask, 
  removeCachedTask, 
  addCachedTask,
  isCacheValid,
  getCacheStats 
} from '../../utils/taskCache';

import {
  Search,
  Filter,
  Plus,
  CheckCircle2,
  Clock,
  AlertCircle,
  Users,
  Calendar,
  ChevronDown,
  MoreVertical,
  Tag,
  Trash2,
  Edit2,
  Eye,
  X,
  FolderKanban,
  Tags,
  Paperclip,
  Mail,
  Phone,
  Building2,
  ArrowLeft,
  LayoutGrid,
  RotateCcw,
  List,
  GitBranch,
  Shield,
  User,
  Info,
  Play,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { createNotification } from '../../lib/notificationService';
import { 
  notifyTaskCompleted, 
  notifyTaskDeleted,
  notifyTaskUpdated 
} from '../../lib/notificationService';
import TaskNotes from './TaskNotes';

// Supabase istemcisini oluştur
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const TASK_STATUS = [
  { id: 'all', label: 'Tümü', color: 'gray' },
  { id: 'todo', label: 'Yapılacak', color: 'yellow' },
  { id: 'in_progress', label: 'Devam Ediyor', color: 'blue' },
  { id: 'review', label: 'İncelemede', color: 'purple' },
  { id: 'done', label: 'Tamamlandı', color: 'green' },
];

// Skeleton Loading Component
const TaskSkeleton = ({ isDetailedView = false }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse ${isDetailedView ? 'h-48' : 'h-32'}`}>
    <div className="flex items-start justify-between mb-3">
      <div className="flex-1">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
      </div>
      <div className="flex gap-2">
        <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
        <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
      </div>
    </div>
    
    <div className="space-y-2 mb-3">
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
    </div>
    
    <div className="flex items-center justify-between">
      <div className="flex gap-2">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16"></div>
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-20"></div>
      </div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
    </div>
  </div>
);

// Skeleton Loading Grid
const TaskSkeletonGrid = ({ count = 6, isDetailedView = false }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array.from({ length: count }).map((_, index) => (
      <TaskSkeleton key={index} isDetailedView={isDetailedView} />
    ))}
  </div>
);

// Smart No Tasks Message Component
const NoTasksMessage = ({ 
  filteredTasks, 
  allTasks, 
  searchQuery, 
  statusFilter, 
  priorityFilter, 
  completionFilter, 
  selectedProject, 
  dateRangeFilter, 
  hasNotesFilter 
}) => {
  const hasActiveFilters = searchQuery || 
    statusFilter !== 'all' || 
    priorityFilter !== 'all' || 
    completionFilter !== 'all' || 
    selectedProject !== 'all' || 
    (dateRangeFilter.start && dateRangeFilter.end) || 
    hasNotesFilter !== 'all';

  const getActiveFilterNames = () => {
    const filters = [];
    if (searchQuery) filters.push(`"${searchQuery}" araması`);
    if (statusFilter !== 'all') {
      const status = TASK_STATUS.find(s => s.id === statusFilter);
      filters.push(status ? status.label : 'durum');
    }
    if (priorityFilter !== 'all') {
      const priority = TASK_PRIORITY.find(p => p.id === priorityFilter);
      filters.push(priority ? priority.label : 'öncelik');
    }
    if (completionFilter !== 'all') {
      filters.push(completionFilter === 'completed' ? 'Tamamlananlar' : 'Tamamlanmayanlar');
    }
    if (selectedProject !== 'all') {
      filters.push('proje');
    }
    if (dateRangeFilter.start && dateRangeFilter.end) {
      filters.push('tarih aralığı');
    }
    if (hasNotesFilter !== 'all') {
      filters.push('rapor durumu');
    }
    return filters;
  };

  if (allTasks.length === 0) {
    return (
      <div className="text-center py-12">
        <FolderKanban className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Henüz Görev Oluşturulmamış
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          İlk görevinizi oluşturarak başlayın
        </p>
      </div>
    );
  }

  if (hasActiveFilters && filteredTasks.length === 0) {
    const activeFilters = getActiveFilterNames();
    return (
      <div className="text-center py-12">
        <Search className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Filtrelenmiş Görev Bulunamadı
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          <span className="font-medium">{activeFilters.join(', ')}</span> kriterlerine uygun görev bulunamadı.
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Filtreleri değiştirerek tekrar deneyin veya tüm görevleri görüntüleyin.
        </p>
      </div>
    );
  }

  return null;
};

const TASK_PRIORITY = [
  { id: 'all', label: 'Tüm Öncelikler', color: 'gray' },
  { id: 'high', label: 'Yüksek', color: 'red' },
  { id: 'medium', label: 'Orta', color: 'yellow' },
  { id: 'low', label: 'Düşük', color: 'green' },
];

const SORT_OPTIONS = [
  { id: 'start_date_asc', label: 'Başlangıç Tarihi (Yakın)', field: 'start_date', order: 'asc' },
  { id: 'start_date_desc', label: 'Başlangıç Tarihi (Uzak)', field: 'start_date', order: 'desc' },
  { id: 'remaining_time_asc', label: 'Kalan Süre (Az)', field: 'remaining_time', order: 'asc' },
  { id: 'remaining_time_desc', label: 'Kalan Süre (Çok)', field: 'remaining_time', order: 'desc' },
  { id: 'created_desc', label: 'En Yeni Oluşturulan', field: 'created_at', order: 'desc' },
  { id: 'created_asc', label: 'En Eski Oluşturulan', field: 'created_at', order: 'asc' },
  { id: 'due_desc', label: 'Bitiş Tarihi (Yakın)', field: 'end_date', order: 'asc' },
  { id: 'due_asc', label: 'Bitiş Tarihi (Uzak)', field: 'end_date', order: 'desc' },
  { id: 'priority_desc', label: 'Öncelik (Yüksek-Düşük)', field: 'priority', order: 'desc' },
  { id: 'priority_asc', label: 'Öncelik (Düşük-Yüksek)', field: 'priority', order: 'asc' },
];

const VIEW_OPTIONS = [
  { id: 'grid', label: 'Grid Görünümü', icon: LayoutGrid },
  { id: 'list', label: 'Liste Görünümü', icon: List },
];

const Tasks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isTaskDetailsLoading, setIsTaskDetailsLoading] = useState(false); // Add this new state
  const [tasks, setTasks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Cache-related states
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheStats, setCacheStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [hasNotesFilter, setHasNotesFilter] = useState('all'); // New state for tasks with notes
  const [completionFilter, setCompletionFilter] = useState('all'); // New state for completion filter: 'all', 'completed', 'not_completed'
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [sortOption, setSortOption] = useState('due_desc');
  const [selectedProject, setSelectedProject] = useState('all');
  const [projects, setProjects] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // Add user role state
  const [showOnlyMyTasks, setShowOnlyMyTasks] = useState(false); // Toggle for super users
  const [viewMode, setViewMode] = useState(() => {
    // localStorage'dan görünüm modunu oku, yoksa 'grid' kullan
    return localStorage.getItem('taskViewMode') || 'grid';
  });
  const [detailedView, setDetailedView] = useState(() => {
    // localStorage'dan detaylı görünüm tercihini oku, yoksa false kullan
    return localStorage.getItem('taskDetailedView') === 'true';
  });
  const [isAssignMemberModalOpen, setIsAssignMemberModalOpen] = useState(false);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    project_id: '',
    sub_project_id: '',
    category_id: [],
    priority: 'medium',
    start_date: '',
    end_date: '',
    notes: '',
  });
  const [editErrors, setEditErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editSelectedCategories, setEditSelectedCategories] = useState([]);
  const [editSelectedMembers, setEditSelectedMembers] = useState([]);
  const [tempSelectedMembers, setTempSelectedMembers] = useState([]); // Temporary selection state
  const [editFiles, setEditFiles] = useState([]);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [subProjects, setSubProjects] = useState({});
  const [categories, setCategories] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Add these state declarations after other state declarations
  const [consultingFirms, setConsultingFirms] = useState([]);
  const [consultingProjects, setConsultingProjects] = useState({});

  // Cache'den görevleri yükle (anında)
  const loadCachedTasks = () => {
    const cachedData = getCachedTasks();
    if (cachedData && cachedData.tasks) {
      setTasks(cachedData.tasks);
      setCacheStats(getCacheStats());
      return true;
    }
    return false;
  };

  // Görevleri veritabanından çek (cache sistemi ile)
  const fetchTasks = async (forceRefresh = false) => {
    try {
      // İlk yükleme ve cache geçerliyse cache'den göster
      if (isInitialLoad && !forceRefresh && isCacheValid()) {
        const hasCachedTasks = loadCachedTasks();
        if (hasCachedTasks) {
          setIsInitialLoad(false);
          setIsLoading(false);
          
          // Arka planda güncel veriyi çek (kullanıcıyı beklemeden)
          setTimeout(() => fetchTasksFromAPI(true), 100);
          return;
        }
      }

      // Cache geçersiz veya zorla yenileme
      await fetchTasksFromAPI(forceRefresh);
      
    } catch (error) {
      console.error('Error in fetchTasks:', error);
      
      // Hata durumunda cache'den yükle
      if (isInitialLoad) {
        const hasCachedTasks = loadCachedTasks();
        if (hasCachedTasks) {
          setIsInitialLoad(false);
          setIsLoading(false);
          return;
        }
      }
      
      toast.error('Görevler yüklenirken bir hata oluştu');
    } finally {
      if (isInitialLoad) {
        setIsInitialLoad(false);
        setIsLoading(false);
      }
      if (isRefreshing) {
        setIsRefreshing(false);
      }
    }
  };

  // API'den görevleri çek
  const fetchTasksFromAPI = async (updateCache = true) => {
    try {
      setIsRefreshing(true);
      
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          project:projects(id, name),
          sub_project:sub_projects(id, name),
          task_categories(
            category:categories(id, name, color)
          ),
          consulting_project:consulting_sub_projects(
            id,
            name,
            parent_consulting:consulting(id, name)
          ),
          task_notes(id)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Görev sahiplerinin ve tamamlayan kişilerin bilgilerini al
      if (data && data.length > 0) {
        const userIds = [...new Set([
          ...data.map(task => task.created_by),
          ...data.filter(task => task.completed_by).map(task => task.completed_by)
        ])];

        const { data: users, error: usersError } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, profile_photo_url, job_position')
          .in('id', userIds);

        if (usersError) throw usersError;

        // Görev verilerine kullanıcı bilgilerini ve kategorileri düzenlenmiş şekilde ekle
        const tasksWithUsers = data.map(task => ({
          ...task,
          created_by_user: users.find(user => user.id === task.created_by),
          completed_by_user: task.completed_by ? users.find(user => user.id === task.completed_by) : null,
          categories: task.task_categories.map(tc => tc.category),
          // Consulting project için proje ve alt proje bilgilerini düzenle
          project: task.consulting_project ? {
            id: task.consulting_project.parent_consulting.id,
            name: task.consulting_project.parent_consulting.name,
            is_consulting: true
          } : task.project,
          sub_project: task.consulting_project ? {
            id: task.consulting_project.id,
            name: task.consulting_project.name,
            is_consulting: true
          } : task.sub_project,
          has_notes: task.task_notes && task.task_notes.length > 0
        }));

        // Debug: Raporu olan görevleri say
        const tasksWithNotes = tasksWithUsers.filter(task => task.has_notes);
        // console.log('Toplam görev sayısı:', tasksWithUsers.length);
        // console.log('Raporu olan görev sayısı:', tasksWithNotes.length);
        // console.log('Raporu olan görevler:', tasksWithNotes.map(task => ({ id: task.id, title: task.title, notesCount: task.task_notes?.length })));

        setTasks(tasksWithUsers);
        
        // Cache'e kaydet
        if (updateCache) {
          setCachedTasks(tasksWithUsers, {
            totalCount: tasksWithUsers.length,
            completedCount: tasksWithUsers.filter(t => t.status === 'done').length
          });
          setCacheStats(getCacheStats());
        }
      } else {
        setTasks([]);
        if (updateCache) {
          setCachedTasks([]);
          setCacheStats(getCacheStats());
        }
      }
    } catch (error) {
      console.error('Error fetching tasks from API:', error);
      throw error;
    }
  };

  // Görev detaylarını getir
  const fetchTaskDetails = async (taskId) => {
    try {
      setIsTaskDetailsLoading(true); // Start loading

      // Önce görevi ve ilişkili verileri çekelim
      const { data: task, error: taskError } = await supabase
        .from('tasks')
        .select(`
          *,
          project:projects(id, name),
          sub_project:sub_projects(id, name),
          task_categories(
            category:categories(id, name, color)
          ),
          consulting_project:consulting_sub_projects(
            id,
            name,
            parent_consulting:consulting(id, name)
          )
        `)
        .eq('id', taskId)
        .single();

      if (taskError) throw taskError;

      // Kategorileri düzenle
      task.categories = task.task_categories.map(tc => tc.category);

      // Consulting project için proje ve alt proje bilgilerini düzenle
      if (task.consulting_project) {
        task.project = {
          id: task.consulting_project.parent_consulting.id,
          name: task.consulting_project.parent_consulting.name,
          is_consulting: true
        };
        task.sub_project = {
          id: task.consulting_project.id,
          name: task.consulting_project.name,
          is_consulting: true
        };
      }

      // Şimdi görev sahibinin ve tamamlayan kişinin bilgilerini alalım
      const userIds = [task.created_by];
      if (task.completed_by) userIds.push(task.completed_by);

      const { data: userData, error: userError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, profile_photo_url, job_position')
        .in('id', userIds);

      if (userError) {
        console.error('Error fetching user details:', userError);
      } else {
        task.created_by_user = userData.find(user => user.id === task.created_by);
        if (task.completed_by) {
          task.completed_by_user = userData.find(user => user.id === task.completed_by);
        }
      }

      setSelectedTask(task);
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error('Görev detayları yüklenirken bir hata oluştu');
    } finally {
      // Add a small delay for better UX before showing content
      setTimeout(() => {
        setIsTaskDetailsLoading(false);
      }, 600);
    }
  };

  // Üye detaylarını getir
  const fetchMemberDetails = async (memberId) => {
    try {
      const { data: member, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', memberId)
        .single();

      if (error) throw error;

      setSelectedMember(member);
      setIsMemberModalOpen(true);
    } catch (error) {
      console.error('Error fetching member details:', error);
      toast.error('Üye detayları yüklenirken bir hata oluştu');
    }
  };

  // Permission check helper function
  const checkPermission = (task, action) => {
    if (!currentUser || !userRole) return false;
    
    switch (action) {
      case 'view':
        // Super users can view all tasks
        if (userRole === 'super') return true;
        
        // Admin users can view tasks they created or they're assigned to
        if (userRole === 'admin') {
          return task.created_by === currentUser.id || 
                (task.assigned_members && 
                 task.assigned_members.some(member => member.id === currentUser.id));
        }
        
        // Regular users can only view tasks they're assigned to
        return task.assigned_members && 
               task.assigned_members.some(member => member.id === currentUser.id);
      
      case 'edit':
        // Super users can edit any task
        if (userRole === 'super') return true;
        
        // Admin users can only edit tasks they created
        if (userRole === 'admin') {
          return task.created_by === currentUser.id;
        }
        
        // Regular users can't edit tasks
        return false;
      
      case 'delete':
        // Super users can delete any task
        if (userRole === 'super') return true;
        
        // Admin users can only delete tasks they created
        if (userRole === 'admin') {
          return task.created_by === currentUser.id;
        }
        
        // Regular users can't delete tasks
        return false;
      
      case 'edit_notes':
        // Super users can edit any task notes
        if (userRole === 'super') return true;
        
        // Admin users can edit notes on tasks they created or their own notes
        if (userRole === 'admin') {
          return task.created_by === currentUser.id;
        }
        
        // Regular users can only edit their own notes
        return false;
      
      case 'delete_notes':
        // Super users can delete any task notes
        if (userRole === 'super') return true;
        
        // Admin users can delete their own notes or notes on tasks they created
        if (userRole === 'admin') {
          return task.created_by === currentUser.id;
        }
        
        // Regular users can't delete notes
        return false;
        
      default:
        return false;
    }
  };

  // Initialize user and permissions
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        setCurrentUser(userProfile);
        setUserRole(userProfile.role); // Set user role
        
        // If user is not super, force them to see only their tasks
        if (userProfile.role !== 'super') {
          setShowOnlyMyTasks(true);
        }
      }
    };
    getCurrentUser();

    // Realtime subscription for user role changes
    let userRoleSubscription;
    const setupRoleSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        userRoleSubscription = supabase
          .channel('user_role_changes')
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'user_profiles',
              filter: `id=eq.${user.id}`
            },
            (payload) => {
              // console.log('User role updated:', payload.new.role);
              
              const newRole = payload.new.role;
              
              // Önceki role değerini current state'ten al
              setUserRole((prevRole) => {
                // Sadece gerçekten değişmişse ve önceki role tanımlıysa bildirim göster
                if (prevRole && prevRole !== newRole) {
                  let roleName = "";
                  if (newRole === "super") roleName = "Süper Yönetici";
                  else if (newRole === "admin") roleName = "Yönetici";
                  else roleName = "Standart Kullanıcı";
                  
                  toast.success(`Yetki seviyeniz değiştirildi! Yeni yetkiniz: ${roleName}`, {
                    duration: 5000,
                    position: 'top-center',
                    icon: '🔐',
                  });
                }
                
                return newRole;
              });
              
              // Update viewing options if role changes
              if (newRole !== 'super' && !showOnlyMyTasks) {
                setShowOnlyMyTasks(true);
              }
            }
          )
          .subscribe();
      }
    };

    setupRoleSubscription();

    // Cleanup subscription on component unmount
    return () => {
      if (userRoleSubscription) {
        supabase.removeChannel(userRoleSubscription);
      }
    };
  }, []); // Dependency array'i boş bırakıyoruz

  // Realtime updates
  useEffect(() => {
    fetchTasks();

    // Realtime subscription for tasks
    const tasksSubscription = supabase
      .channel('tasks_channel')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'tasks' }, 
        async (payload) => {
          const { data: newTask, error } = await supabase
            .from('tasks')
            .select(`
              *,
              project:projects(id, name),
              sub_project:sub_projects(id, name),
              task_categories(
                category:categories(id, name, color)
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (!error && newTask) {
            // Kullanıcı bilgilerini al
            const { data: userData } = await supabase
              .from('user_profiles')
              .select('id, first_name, last_name, profile_photo_url, job_position')
              .eq('id', newTask.created_by)
              .single();

            const taskWithUser = {
              ...newTask,
              created_by_user: userData,
              categories: newTask.task_categories.map(tc => tc.category)
            };

            setTasks(currentTasks => [taskWithUser, ...currentTasks]);
          }
      })
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'tasks' },
        async (payload) => {
          const { data: updatedTask, error } = await supabase
            .from('tasks')
            .select(`
              *,
              project:projects(id, name),
              sub_project:sub_projects(id, name),
              task_categories(
                category:categories(id, name, color)
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (!error && updatedTask) {
            // Kullanıcı bilgilerini al
            const { data: userData } = await supabase
              .from('user_profiles')
              .select('id, first_name, last_name, profile_photo_url, job_position')
              .eq('id', updatedTask.created_by)
              .single();

            const taskWithUser = {
              ...updatedTask,
              created_by_user: userData,
              categories: updatedTask.task_categories.map(tc => tc.category)
            };

            setTasks(currentTasks => 
              currentTasks.map(task => 
                task.id === payload.new.id ? taskWithUser : task
              )
            );
          }
      })
      .on('postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'tasks' },
        (payload) => {
          setTasks(currentTasks => 
            currentTasks.filter(task => task.id !== payload.old.id)
          );
      })
      .subscribe();

    // İlgili diğer tabloları da dinle
    const relatedChangesSubscription = supabase
      .channel('related_changes')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'task_categories' },
        () => fetchTasks()
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'projects' },
        () => fetchTasks()
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'sub_projects' },
        () => fetchTasks()
      )
      .subscribe();

    // Cleanup subscriptions
    return () => {
      tasksSubscription.unsubscribe();
      relatedChangesSubscription.unsubscribe();
    };
  }, []);

  // Fetch projects for filtering
  const fetchProjects = async () => {
    try {
      // Fetch normal projects (from projects table) - Ana projeler
      const { data: normalProjects, error: normalError } = await supabase
        .from('projects')
        .select(`
          *,
          project_categories (
            categories (*)
          )
        `);

      if (normalError) throw normalError;

      // Fetch sub projects (from sub_projects table) - Alt projeler
      const { data: subProjects, error: subError } = await supabase
        .from('sub_projects')
        .select('*')
        .eq('status', 'active');

      if (subError) throw subError;

      // Fetch consulting projects (from consulting table) - Danışmanlık firmaları
      const { data: consultingFirms, error: consultingError } = await supabase
        .from('consulting')
        .select('id, name');

      if (consultingError) throw consultingError;

      // Fetch consulting sub projects (from consulting_sub_projects table)
      const { data: consultingSubProjects, error: consultingSubError } = await supabase
        .from('consulting_sub_projects')
        .select('id, name, parent_consulting_id');

      if (consultingSubError) throw consultingSubError;

      // Store projects in the same structure as CreateTask
      setProjects(normalProjects || []);
      setSubProjects(prev => {
        const newSubProjects = {};
        (subProjects || []).forEach(sub => {
          if (!newSubProjects[sub.parent_project_id]) {
            newSubProjects[sub.parent_project_id] = [];
          }
          newSubProjects[sub.parent_project_id].push(sub);
        });
        return newSubProjects;
      });
      setConsultingFirms(consultingFirms || []);
      setConsultingProjects(prev => {
        const newConsultingProjects = {};
        (consultingSubProjects || []).forEach(sub => {
          if (!newConsultingProjects[sub.parent_consulting_id]) {
            newConsultingProjects[sub.parent_consulting_id] = [];
          }
          newConsultingProjects[sub.parent_consulting_id].push(sub);
        });
        return newConsultingProjects;
      });
      
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Projeler yüklenirken bir hata oluştu');
    }
  };

  // Kategorileri veritabanından çek
  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('*');
    if (error) {
      console.error('Error fetching categories:', error);
      toast.error('Kategoriler yüklenirken bir hata oluştu');
    } else {
      setCategories(data || []);
    }
  };

  // Add fetchCategories to initial data fetch
  useEffect(() => {
    fetchProjects();
    fetchCategories(); // Add this line to fetch categories when component mounts
    fetchConsultingFirms(); // Add this line
  }, []);

  // Enhanced filtering logic
  const filteredTasks = useMemo(() => {
    let filtered = tasks.filter(task => {
      // Permission-based filtering
      if (!currentUser || !userRole) return false;
      
      // For non-super users or super users with "show only my tasks" enabled
      if (userRole !== 'super' || showOnlyMyTasks) {
        if (userRole === 'super' && showOnlyMyTasks) {
          // Super user with "show only my tasks" enabled - only show tasks where they are a team member
          const isAssigned = task.assigned_members && 
            task.assigned_members.some(member => member.id === currentUser.id);
          if (!isAssigned) return false;
        } else if (userRole === 'admin') {
          // Admin sees tasks they created or are assigned to
          const isAssigned = task.assigned_members && 
            task.assigned_members.some(member => member.id === currentUser.id);
          const isCreator = task.created_by === currentUser.id;
          if (!isAssigned && !isCreator) return false;
        } else if (userRole === 'user') {
          // Regular user sees only tasks they're assigned to
          const isAssigned = task.assigned_members && 
            task.assigned_members.some(member => member.id === currentUser.id);
          if (!isAssigned) return false;
        }
      }
      
      // Search matching
      const matchesSearch = searchQuery === '' || 
        task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchQuery.toLowerCase());

      // Status matching
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;

      // Priority matching
      const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
     
      // Completion matching
      let matchesCompletion = true;
      if (completionFilter === 'completed') {
        matchesCompletion = task.status === 'done' || task.status === 'completed';
      } else if (completionFilter === 'not_completed') {
        matchesCompletion = task.status !== 'done' && task.status !== 'completed';
      }
      
      // Project matching
      let matchesProject = selectedProject === 'all';
      if (!matchesProject) {
        if (selectedProject === 'consulting') {
          // Danışmanlık projesi seçiliyse, danışmanlık görevlerini göster
          matchesProject = task.consulting_parent_id !== null;
        } else if (selectedProject.startsWith('consulting_firm_')) {
          // Belirli bir danışmanlık firması seçiliyse
          const consultingId = selectedProject.replace('consulting_firm_', '');
          matchesProject = task.consulting_parent_id === consultingId;
        } else if (selectedProject.startsWith('consulting_project_')) {
          // Belirli bir danışmanlık alt projesi seçiliyse
          const consultingProjectId = selectedProject.replace('consulting_project_', '');
          matchesProject = task.consulting_project_id === consultingProjectId;
        } else {
          // Normal projeler için - project_id veya sub_project_id ile eşleş
          matchesProject = task.project_id === selectedProject || task.sub_project_id === selectedProject;
        }
      }
      
      // Date range matching
      let matchesDateRange = true;
      if (dateRangeFilter.start && dateRangeFilter.end) {
        const taskDate = new Date(task.end_date);
        const startDate = new Date(dateRangeFilter.start);
        const endDate = new Date(dateRangeFilter.end);
        matchesDateRange = taskDate >= startDate && taskDate <= endDate;
      }

      // Notes filter matching
      let matchesNotes = true;
      if (hasNotesFilter === 'has_notes') {
        matchesNotes = task.has_notes === true;
        // Debug: Rapor filtresi için
        if (task.has_notes) {
          // console.log('Raporu olan görev filtreleniyor:', task.title, 'has_notes:', task.has_notes);
        }
      }

      // TÜM FİLTRELERİN BİRLİKTE ÇALIŞMASI
      return matchesSearch && matchesStatus && matchesPriority && matchesCompletion && matchesProject && matchesDateRange && matchesNotes;
    });

    // Sorting logic
    filtered = filtered.sort((a, b) => {
      const sortOpt = SORT_OPTIONS.find(opt => opt.id === sortOption);
      if (!sortOpt) return 0;

      let comparison = 0;
      switch (sortOpt.field) {
        case 'created_at':
          comparison = new Date(a.created_at) - new Date(b.created_at);
          break;
        case 'end_date':
          comparison = new Date(a.end_date) - new Date(b.end_date);
          break;
        case 'start_date':
          comparison = new Date(a.start_date) - new Date(b.start_date);
          break;
        case 'remaining_time':
          // Calculate remaining time for both tasks
          const now = new Date();
          const aEndDate = new Date(a.end_date);
          const bEndDate = new Date(b.end_date);
          const aRemaining = aEndDate - now;
          const bRemaining = bEndDate - now;
          comparison = aRemaining - bRemaining;
          break;
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        default:
          comparison = 0;
      }
      return sortOpt.order === 'desc' ? -comparison : comparison;
    });

    // Debug: Filtreleme sonucu
    if (hasNotesFilter === 'has_notes') {
      // console.log('Rapor filtresi aktif - Filtrelenmiş görev sayısı:', filtered.length);
      // console.log('Filtrelenmiş görevler:', filtered.map(task => ({ id: task.id, title: task.title, has_notes: task.has_notes })));
    }

    return filtered;
  }, [tasks, searchQuery, statusFilter, priorityFilter, completionFilter, selectedProject, dateRangeFilter, hasNotesFilter, currentUser, userRole, showOnlyMyTasks, sortOption]);

  // localStorage'a görünüm tercihlerini kaydet
  useEffect(() => {
    localStorage.setItem('taskViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('taskDetailedView', detailedView.toString());
  }, [detailedView]);

  // İlk yüklemede localStorage değerlerini kontrol et ve kullanıcıyı bilgilendir
  useEffect(() => {
    const savedViewMode = localStorage.getItem('taskViewMode');
    const savedDetailedView = localStorage.getItem('taskDetailedView');
    
    // Sadece kaydedilmiş tercihler varsa ve varsayılan değerlerden farklıysa bilgilendirme göster
    if (savedViewMode && savedViewMode !== 'grid') {
      setTimeout(() => {
        // toast.success(`Son görünüm tercihiniz (${savedViewMode === 'list' ? 'Liste' : 'Grid'}) hatırlandı`);
      }, 1000);
    } else if (savedDetailedView === 'true') {
      setTimeout(() => {
        // toast.success('Detaylı görünüm tercihiniz hatırlandı');
      }, 1000);
    }
  }, []);

  const getStatusColor = (status) => {
    const statusObj = TASK_STATUS.find(s => s.id === status);
    return statusObj ? statusObj.color : 'gray';
  };

  const getPriorityColor = (priority) => {
    const priorityObj = TASK_PRIORITY.find(p => p.id === priority);
    return priorityObj ? priorityObj.color : 'gray';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Detaylı tarih formatı - yıl ay gün saat dakika saniye
  const formatDetailedDate = (date) => {
    return new Date(date).toLocaleString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Yükleme durumu için skeleton
  const TaskSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="animate-pulse space-y-3">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
        </div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        <div className="flex items-center justify-between pt-2">
          <div className="flex space-x-2">
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
          </div>
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
        </div>
      </div>
    </div>
  );

  const TaskCard = ({ task, detailedView }) => {
    // Calculate remaining and elapsed time
    const calculateTimeInfo = () => {
      const now = new Date();
      const endDate = new Date(task.end_date);
      const createdDate = new Date(task.created_at);
      
      // Set end date time to match creation time
      endDate.setHours(createdDate.getHours());
      endDate.setMinutes(createdDate.getMinutes());
      endDate.setSeconds(createdDate.getSeconds());
      endDate.setMilliseconds(createdDate.getMilliseconds());
      
      const timeDiff = endDate - now;
      const elapsedTimeDiff = now - createdDate;
      
      // Calculate remaining time
      let remainingText = '';
      let remainingColor = 'text-gray-500 dark:text-gray-400';
      
      if (timeDiff <= 0) {
        const overdueDays = Math.ceil(Math.abs(timeDiff) / (1000 * 60 * 60 * 24));
        remainingText = `${overdueDays}g gecikti!`;
        remainingColor = 'text-red-600 dark:text-red-400';
      } else {
        const daysRemaining = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hoursRemaining = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutesRemaining = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (daysRemaining > 5) {
          remainingColor = 'text-green-600 dark:text-green-400';
        } else if (daysRemaining > 2) {
          remainingColor = 'text-yellow-600 dark:text-yellow-400';
        } else if (daysRemaining > 1) {
          remainingColor = 'text-orange-600 dark:text-orange-400';
        } else {
          remainingColor = 'text-red-600 dark:text-red-400';
        }
        
        remainingText = daysRemaining > 0 
          ? `${daysRemaining}g ${hoursRemaining}s`
          : hoursRemaining > 0 
            ? `${hoursRemaining}s ${minutesRemaining}dk`
            : `${minutesRemaining}dk`;
      }
      
      // Calculate elapsed time
      let elapsedText = '';
      if (elapsedTimeDiff > 0) {
        const daysElapsed = Math.floor(elapsedTimeDiff / (1000 * 60 * 60 * 24));
        const hoursElapsed = Math.floor((elapsedTimeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutesElapsed = Math.floor((elapsedTimeDiff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (daysElapsed > 0) {
          elapsedText = `${daysElapsed}g ${hoursElapsed}s`;
        } else if (hoursElapsed > 0) {
          elapsedText = `${hoursElapsed}s ${minutesElapsed}dk`;
        } else {
          elapsedText = `${minutesElapsed}dk`;
        }
      }
      
      return { remainingText, remainingColor, elapsedText };
    };

    // Mouse tracking for light effect
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setMousePosition({ x, y });
    };

    const { remainingText, remainingColor, elapsedText } = calculateTimeInfo();

    return (
      <motion.div
        onClick={() => {
          if (checkPermission(task, 'view')) {
            setIsTaskModalOpen(true);
            setSelectedTask(null);
            fetchTaskDetails(task.id);
          } else {
            toast.error("Bu görevi görüntüleme yetkiniz bulunmamaktadır.");
          }
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          relative overflow-hidden
          bg-white dark:bg-gray-800 
          border border-gray-200 dark:border-gray-700 
          rounded-xl shadow-sm hover:shadow-lg
          transition-all duration-700 ease-out
          transform hover:scale-[1.01]
          cursor-pointer group
        `}
        style={{
          background: isHovered ? 
            `radial-gradient(1200px circle at ${mousePosition.x}px ${mousePosition.y}px, 
              rgba(59, 130, 246, 0.04) 0%, 
              rgba(59, 130, 246, 0.02) 30%, 
              transparent 70%
            )` : ''
        }}
      >
        {/* Subtle Light Overlay */}
        {isHovered && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(800px circle at ${mousePosition.x}px ${mousePosition.y}px, 
                rgba(255, 255, 255, 0.08) 0%, 
                rgba(255, 255, 255, 0.04) 30%, 
                transparent 60%
              )`,
              opacity: 0.6,
              transition: 'opacity 0.6s ease-out',
              animation: 'subtleGlow 2s ease-in-out',
            }}
          />
        )}
        
        {/* Subtle border enhancement */}
        <div
          className={`absolute inset-0 rounded-xl pointer-events-none transition-opacity duration-500 ${
            isHovered ? 'opacity-30' : 'opacity-0'
          }`}
          style={{
            background: `linear-gradient(135deg, transparent 30%, rgba(59, 130, 246, 0.03) 50%, transparent 70%)`,
          }}
        />
        
        {/* Card Content */}
        <div className={`relative z-10 flex flex-col ${detailedView ? 'h-full p-5' : 'min-h-[320px] p-4'}`}>
          {/* Header Section */}
          <div className={`flex items-start justify-between gap-3 mb-4 ${detailedView ? 'h-20' : 'h-20'}`}>
            <div className="flex-1 min-w-0 h-full flex flex-col">
              <h3 className={`font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2 flex-1${
                detailedView ? 'text-lg mb-2' : 'text-sm mb-3'
              }`}>
                {task.title}
                {task.has_notes && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                    <FileText className="w-3 h-3 mr-1" /> Raporu Var
                  </span>
                )}
              </h3>
              
              {/* Status and Priority - Clean Badges */}
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-${getStatusColor(task.status)}-100 dark:bg-${getStatusColor(task.status)}-900/30 text-${getStatusColor(task.status)}-700 dark:text-${getStatusColor(task.status)}-400`}>
                  {TASK_STATUS.find(s => s.id === task.status)?.label}
                </span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-${getPriorityColor(task.priority)}-100 dark:bg-${getPriorityColor(task.priority)}-900/30 text-${getPriorityColor(task.priority)}-700 dark:text-${getPriorityColor(task.priority)}-400`}>
                  {TASK_PRIORITY.find(p => p.id === task.priority)?.label}
                </span>
              </div>
            </div>
            
            {/* Action Button */}
            <button 
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                if (checkPermission(task, 'view')) {
                  setIsTaskModalOpen(true);
                  fetchTaskDetails(task.id);
                } else {
                  toast.error("Bu görevi görüntüleme yetkiniz bulunmamaktadır.");
                }
              }}
            >
              <Eye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Description - Show in both views */}
          {task.description && (
            <div className={`mb-4 flex items-start ${detailedView ? 'h-12' : 'h-10'}`}>
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                {task.description}
              </p>
            </div>
          )}

          {/* Spacer to push bottom content down */}
          <div className="flex-grow"></div>

          {/* Bottom Content with unified background */}
          <div className="bg-blue-50/50 dark:bg-gray-700/20 rounded-lg p-3 space-y-4">
            {/* Date Information Section */}
            <div className="space-y-2">
              {/* Start and End Dates - Only in detailed view */}
              {detailedView && (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-green-500" />
                    <span className="text-gray-500 dark:text-gray-400">Başlangıç Tarihi:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDate(task.start_date)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-red-500" />
                    <span className="text-gray-500 dark:text-gray-400">Bitiş Tarihi:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDate(task.end_date)}
                    </span>
                  </div>

                  {/* Task Status Notification */}
                  {(() => {
                    const now = new Date();
                    const startDate = new Date(task.start_date);
                    const endDate = new Date(task.end_date);
                    
                    let statusText = '';
                    let statusColor = '';
                    let statusIcon = null;
                    
                    // Önce tamamlanma durumunu kontrol et - tamamlanmışsa her zaman "Tamamlandı" göster
                    if (task.status === 'completed' || task.status === 'done') {
                      if (task.completed_at) {
                        statusText = `${formatDistanceToNow(new Date(task.completed_at), { addSuffix: true, locale: tr })} tamamlandı`;
                      } else {
                        statusText = 'Tamamlandı';
                      }
                      statusColor = 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
                      statusIcon = <CheckCircle2 className="w-4 h-4" />;
                    } else if (now < startDate) {
                      statusText = 'Henüz Başlamadı';
                      statusColor = 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
                      statusIcon = <Clock className="w-4 h-4" />;
                    } else if (now > endDate) {
                      statusText = 'Süresi Geçti';
                      statusColor = 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
                      statusIcon = <AlertCircle className="w-4 h-4" />;
                    } else {
                      statusText = 'Devam Ediyor';
                      statusColor = 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
                      statusIcon = <Play className="w-4 h-4" />;
                    }
                    
                    return (
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${statusColor} mt-3`}>
                        {statusIcon}
                        <span>{statusText}</span>
                      </div>
                    );
                  })()}
                </>
              )}
              
              {/* Due Date - Only in compact view */}
              {!detailedView && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-500 dark:text-gray-400">Bitiş Tarihi:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(task.end_date)}
                  </span>
                </div>
              )}
            </div>

            {/* Creation Date - Separate section for detailed view */}
            {detailedView && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-500 dark:text-gray-400">Oluşturulma Tarihi:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(task.created_at)}
                  </span>
                </div>
              </div>
            )}

            {/* Time Information - Only in detailed view */}
            {detailedView && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                <div className="text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {(() => {
                      const now = new Date();
                      const startDate = new Date(task.start_date);
                      return now < startDate ? 'Başlamasına Kalan Süre' : 'Kalan Süre';
                    })()}
                  </div>
                  <div className={`text-sm font-semibold ${remainingColor}`}>
                    {remainingText}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Geçen Süre</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {elapsedText}
                  </div>
                </div>
              </div>
            )}

            {/* Project Section */}
            {task.project && (
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                    task.project.is_consulting
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                  }`}>
                    <FolderKanban className="w-3 h-3 mr-1" />
                    {task.project.name}
                  </span>
                  
                  {task.sub_project && (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                      task.project.is_consulting
                        ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300'
                        : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
                    }`}>
                      {task.sub_project.name}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Categories */}
            {!task.project?.is_consulting && task.categories && task.categories.length > 0 && (
              <div>
                <div className="flex flex-wrap gap-1">
                  {task.categories.slice(0, detailedView ? task.categories.length : 2).map((category) => (
                    <span
                      key={category.id}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-${category.color}-100 dark:bg-${category.color}-900/30 text-${category.color}-700 dark:text-${category.color}-400`}
                    >
                      {category.name}
                    </span>
                  ))}
                  {!detailedView && task.categories.length > 2 && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                      +{task.categories.length - 2}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* People and Progress Section */}
            <div className="space-y-3">
              {/* People Section */}
              <div className="flex items-center justify-between">
                {/* Creator */}
                {task.created_by_user && (
                  <div className="flex items-center gap-2">
                    <img
                      src={task.created_by_user.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(task.created_by_user.first_name + ' ' + task.created_by_user.last_name)}&background=6366f1&color=fff`}
                      alt={`${task.created_by_user.first_name} ${task.created_by_user.last_name}`}
                      className="w-6 h-6 rounded-full ring-2 ring-white dark:ring-gray-800"
                    />
                    <div className="min-w-0">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 block truncate">
                        {task.created_by_user.first_name} {task.created_by_user.last_name}
                        {currentUser && task.created_by_user.id === currentUser.id && (
                          <span className="text-blue-600 dark:text-blue-400 ml-1">(siz)</span>
                        )}
                      </span>
                      {detailedView && task.created_by_user.job_position && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 block truncate">
                          {task.created_by_user.job_position}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Assigned Members - Show in both views */}
                {task.assigned_members && task.assigned_members.length > 0 && (
                  <div className="flex items-center gap-2">
                    {!detailedView && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">Atanan:</span>
                    )}
                    {detailedView && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">Atanan:</span>
                    )}
                    <div className="flex -space-x-1">
                      {task.assigned_members.slice(0, detailedView ? 3 : 2).map((member) => (
                        <img
                          key={member.id}
                          src={member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=10b981&color=fff`}
                          alt={member.name}
                          className="w-6 h-6 rounded-full ring-2 ring-white dark:ring-gray-800"
                          title={member.name}
                        />
                      ))}
                      {((detailedView && task.assigned_members.length > 3) || (!detailedView && task.assigned_members.length > 2)) && (
                        <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 ring-2 ring-white dark:ring-gray-800 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                            +{detailedView ? task.assigned_members.length - 3 : task.assigned_members.length - 2}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {task.progress > 0 && (
                <div>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-gray-500 dark:text-gray-400">İlerleme</span>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">{task.progress}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${task.progress}%` }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const TaskListItem = ({ task }) => {
    // Calculate remaining and elapsed time (same as grid view)
    const calculateTimeInfo = () => {
      const now = new Date();
      const endDate = new Date(task.end_date);
      const createdDate = new Date(task.created_at);
      
      // Set end date time to match creation time
      endDate.setHours(createdDate.getHours());
      endDate.setMinutes(createdDate.getMinutes());
      endDate.setSeconds(createdDate.getSeconds());
      endDate.setMilliseconds(createdDate.getMilliseconds());
      
      const timeDiff = endDate - now;
      const elapsedTimeDiff = now - createdDate;
      
      // Calculate remaining time
      let remainingText = '';
      let remainingColor = 'text-gray-500 dark:text-gray-400';
      
      if (timeDiff <= 0) {
        const overdueDays = Math.ceil(Math.abs(timeDiff) / (1000 * 60 * 60 * 24));
        remainingText = `${overdueDays}g gecikti!`;
        remainingColor = 'text-red-600 dark:text-red-400';
      } else {
        const daysRemaining = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hoursRemaining = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutesRemaining = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (daysRemaining > 5) {
          remainingColor = 'text-green-600 dark:text-green-400';
        } else if (daysRemaining > 2) {
          remainingColor = 'text-yellow-600 dark:text-yellow-400';
        } else if (daysRemaining > 1) {
          remainingColor = 'text-orange-600 dark:text-orange-400';
        } else {
          remainingColor = 'text-red-600 dark:text-red-400';
        }
        
        remainingText = daysRemaining > 0 
          ? `${daysRemaining}g ${hoursRemaining}s`
          : hoursRemaining > 0 
            ? `${hoursRemaining}s ${minutesRemaining}dk`
            : `${minutesRemaining}dk`;
      }
      
      // Calculate elapsed time
      let elapsedText = '';
      if (elapsedTimeDiff > 0) {
        const daysElapsed = Math.floor(elapsedTimeDiff / (1000 * 60 * 60 * 24));
        const hoursElapsed = Math.floor((elapsedTimeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutesElapsed = Math.floor((elapsedTimeDiff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (daysElapsed > 0) {
          elapsedText = `${daysElapsed}g ${hoursElapsed}s`;
        } else if (hoursElapsed > 0) {
          elapsedText = `${hoursElapsed}s ${minutesElapsed}dk`;
        } else {
          elapsedText = `${minutesElapsed}dk`;
        }
      }
      
      return { remainingText, remainingColor, elapsedText };
    };

    const { remainingText, remainingColor, elapsedText } = calculateTimeInfo();

    return (
      <motion.div
        whileHover={{ scale: 1.01, y: -2 }}
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-all duration-300 cursor-pointer"
        onClick={() => {
          if (checkPermission(task, 'view')) {
            setIsTaskModalOpen(true);
            setSelectedTask(null);
            fetchTaskDetails(task.id);
          } else {
            toast.error("Bu görevi görüntüleme yetkiniz bulunmamaktadır.");
          }
        }}
      >
        <div className="grid grid-cols-12 gap-6 items-start">
          {/* Left Column - Task Info */}
          <div className="col-span-12 lg:col-span-7 space-y-4">
            {/* Header */}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight flex-1">
                  {task.title}
                  {task.has_notes && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      <FileText className="w-3 h-3 mr-1" /> Raporu Var
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-${getStatusColor(task.status)}-100 dark:bg-${getStatusColor(task.status)}-900/30 text-${getStatusColor(task.status)}-700 dark:text-${getStatusColor(task.status)}-400`}>
                    {TASK_STATUS.find(s => s.id === task.status)?.label}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-${getPriorityColor(task.priority)}-100 dark:bg-${getPriorityColor(task.priority)}-900/30 text-${getPriorityColor(task.priority)}-700 dark:text-${getPriorityColor(task.priority)}-400`}>
                    {TASK_PRIORITY.find(p => p.id === task.priority)?.label}
                  </span>
                </div>
              </div>

              {/* Description */}
              {task.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {task.description}
                </p>
              )}
            </div>

            {/* Date Information */}
            <div className="bg-gray-50/50 dark:bg-gray-700/20 rounded-lg p-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-green-500" />
                  <span className="text-gray-500 dark:text-gray-400">Başlangıç:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(task.start_date)}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-red-500" />
                  <span className="text-gray-500 dark:text-gray-400">Bitiş:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(task.end_date)}
                  </span>
                </div>
              </div>

              {/* Task Status Notification */}
              {(() => {
                const now = new Date();
                const startDate = new Date(task.start_date);
                const endDate = new Date(task.end_date);
                
                let statusText = '';
                let statusColor = '';
                let statusIcon = null;
                
                // Önce tamamlanma durumunu kontrol et - tamamlanmışsa her zaman "Tamamlandı" göster
                if (task.status === 'completed' || task.status === 'done') {
                  if (task.completed_at) {
                    statusText = `${formatDistanceToNow(new Date(task.completed_at), { addSuffix: true, locale: tr })} tamamlandı`;
                  } else {
                    statusText = 'Tamamlandı';
                  }
                  statusColor = 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
                  statusIcon = <CheckCircle2 className="w-4 h-4" />;
                } else if (now < startDate) {
                  statusText = 'Henüz Başlamadı';
                  statusColor = 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
                  statusIcon = <Clock className="w-4 h-4" />;
                } else if (now > endDate) {
                  statusText = 'Süresi Geçti';
                  statusColor = 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
                  statusIcon = <AlertCircle className="w-4 h-4" />;
                } else {
                  statusText = 'Devam Ediyor';
                  statusColor = 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
                  statusIcon = <Play className="w-4 h-4" />;
                }
                
                return (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${statusColor}`}>
                    {statusIcon}
                    <span>{statusText}</span>
                  </div>
                );
              })()}

              {/* Creation Date */}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-500 dark:text-gray-400">Oluşturulma:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(task.created_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* Project and Categories */}
            <div className="space-y-3">
              {/* Project Section */}
              {task.project && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                    task.project.is_consulting
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                  }`}>
                    <FolderKanban className="w-3 h-3 mr-1" />
                    {task.project.name}
                  </span>
                  
                  {task.sub_project && (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                      task.project.is_consulting
                        ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300'
                        : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
                    }`}>
                      {task.sub_project.name}
                    </span>
                  )}
                </div>
              )}

              {/* Categories */}
              {!task.project?.is_consulting && task.categories && task.categories.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {task.categories.map((category) => (
                    <span
                      key={category.id}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-${category.color}-100 dark:bg-${category.color}-900/30 text-${category.color}-700 dark:text-${category.color}-400`}
                    >
                      {category.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Team & Progress */}
          <div className="col-span-12 lg:col-span-5 flex flex-col space-y-2">
            {/* Time Information */}
            <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {(() => {
                      const now = new Date();
                      const startDate = new Date(task.start_date);
                      return now < startDate ? 'Başlamasına Kalan Süre' : 'Kalan Süre';
                    })()}
                  </div>
                  <div className={`text-sm font-semibold ${remainingColor}`}>
                    {remainingText}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Geçen Süre</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {elapsedText}
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            {task.progress > 0 && (
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-500 dark:text-gray-400">İlerleme</span>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">{task.progress}%</span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${task.progress}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
                  />
                </div>
              </div>
            )}

            {/* Spacer to push content to bottom */}
            <div className="flex-1"></div>

            {/* People Section - Bottom Aligned */}
            <div className="space-y-3">
              {/* Creator */}
              {task.created_by_user && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Görevi Oluşturan:</div>
                  <div className="flex items-center gap-2">
                    <img
                      src={task.created_by_user.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(task.created_by_user.first_name + ' ' + task.created_by_user.last_name)}&background=6366f1&color=fff`}
                      alt={`${task.created_by_user.first_name} ${task.created_by_user.last_name}`}
                      className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-gray-800"
                    />
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block truncate">
                        {task.created_by_user.first_name} {task.created_by_user.last_name}
                        {currentUser && task.created_by_user.id === currentUser.id && (
                          <span className="text-blue-600 dark:text-blue-400 ml-1">(siz)</span>
                        )}
                      </span>
                      {task.created_by_user.job_position && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 block truncate">
                          {task.created_by_user.job_position}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Assigned Members */}
              {task.assigned_members && task.assigned_members.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Atanan Üyeler:</span>
                    <span className="text-xs text-gray-400">{task.assigned_members.length} üye</span>
                  </div>
                  <div className="flex -space-x-2">
                    {task.assigned_members.slice(0, 4).map((member) => (
                      <img
                        key={member.id}
                        src={member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=10b981&color=fff`}
                        alt={member.name}
                        className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-gray-800"
                        title={member.name}
                      />
                    ))}
                    {task.assigned_members.length > 4 && (
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 ring-2 ring-white dark:ring-gray-800 flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                          +{task.assigned_members.length - 4}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Action Button - Bottom Aligned */}
            <div className="pt-2">
              <button 
                className="w-full px-4 py-2 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  if (checkPermission(task, 'view')) {
                    setIsTaskModalOpen(true);
                    fetchTaskDetails(task.id);
                  } else {
                    toast.error("Bu görevi görüntüleme yetkiniz bulunmamaktadır.");
                  }
                }}
              >
                <Eye className="w-4 h-4" />
                Detayları Görüntüle
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Kullanıcıları getir
  const fetchAvailableMembers = async () => {
    try {
      const { data: members, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('first_name', { ascending: true });

      if (error) throw error;

      setAvailableMembers(members);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Kullanıcılar yüklenirken bir hata oluştu');
    }
  };

  // Ekip üyelerini göreve ata
  const assignMembersToTask = async () => {
    try {
      setIsAssigning(true);

      // Update task with new assigned members
      const { error: updateError } = await supabase
        .from('tasks')
        .update({
          assigned_members: selectedMembers.map(member => ({
            id: member.id,
            name: `${member.first_name} ${member.last_name}`,
            avatar: member.profile_photo_url,
          }))
        })
        .eq('id', selectedTask.id);

      if (updateError) {
        throw updateError;
      }

      // Başarılı mesajı göster
      toast.success('Ekip üyeleri başarıyla atandı');
      
      // Modal'ı kapat ve state'i sıfırla
      setIsAssignMemberModalOpen(false);
      setSelectedMembers([]);
      setMemberSearchQuery('');
      
      // Görev listesini ve görev detaylarını güncelle
      await Promise.all([
        fetchTasks(),
        fetchTaskDetails(selectedTask.id)
      ]);

    } catch (error) {
      console.error('Error assigning members:', error);
      toast.error('Ekip üyeleri atanırken bir hata oluştu');
    } finally {
      setIsAssigning(false);
    }
  };

  // Üye seçimi
  const toggleMemberSelection = (member) => {
    setSelectedMembers(prev => {
      const isSelected = prev.some(m => m.id === member.id);
      if (isSelected) {
        return prev.filter(m => m.id !== member.id);
      } else {
        return [...prev, member];
      }
    });
  };

  // Filtrelenmiş üyeler
  const filteredMembers = availableMembers.filter(member =>
    `${member.first_name} ${member.last_name}`.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
    member.email?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
    member.job_position?.toLowerCase().includes(memberSearchQuery.toLowerCase())
  );

  // Özel proje tipleri için kontrol
  const SPECIAL_PROJECT_TYPES = ['Genel', 'Muhasebe', 'Finans'];

  // Seçili projenin özel tip olup olmadığını kontrol et
  const isSpecialProjectType = (projectId) => {
    const selectedProject = projects.find(p => p.id === projectId);
    return selectedProject && SPECIAL_PROJECT_TYPES.includes(selectedProject.name);
  };

  // Validate edit form
  const validateEditForm = () => {
    const newErrors = {};

    // Title is required for all tasks
    if (!editFormData.title.trim()) {
      newErrors.title = 'Görev başlığı zorunludur';
    }

    // Project selection is required
    if (!editFormData.project_id) {
      newErrors.project_id = 'Proje seçimi zorunludur';
    }

    // Validate based on project type
    if (editFormData.project_id) {
      if (editFormData.project_id === 'consulting') {
        // For consulting projects, both firm and project are required
        if (!editFormData.consulting_id) {
          newErrors.consulting_id = 'Danışmanlık firması seçimi zorunludur';
        }
        if (!editFormData.consulting_project_id) {
          newErrors.consulting_project_id = 'Firma projesi seçimi zorunludur';
        }
      } else {
        // For normal projects, validate categories if applicable
        const selectedProject = projects.find(p => p.id === editFormData.project_id);
        if (!isSpecialProjectType(editFormData.project_id) && 
            selectedProject?.name !== 'Bakım' && 
            editFormData.category_id.length === 0) {
          newErrors.category_id = 'En az bir kategori seçimi zorunludur';
        }
      }
    }

    // Dates are required for all tasks
    if (!editFormData.start_date) {
      newErrors.start_date = 'Başlangıç tarihi zorunludur';
    }

    if (!editFormData.end_date) {
      newErrors.end_date = 'Bitiş tarihi zorunludur';
    }

    if (editFormData.start_date && editFormData.end_date && 
        new Date(editFormData.start_date) > new Date(editFormData.end_date)) {
      newErrors.end_date = 'Bitiş tarihi başlangıç tarihinden önce olamaz';
    }

    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value || '', // Ensure null values become empty strings
    }));

    // Clear error when user starts typing
    if (editErrors[name]) {
      setEditErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Handle edit date changes with validation
  const handleEditDateChange = (e) => {
    const { name, value } = e.target;
    const otherDateField = name === 'start_date' ? 'end_date' : 'start_date';
    const otherDateValue = editFormData[otherDateField];

    if (name === 'start_date' && otherDateValue && value > otherDateValue) {
      toast.error('Başlangıç tarihi, bitiş tarihinden sonra olamaz!');
      return;
    }

    if (name === 'end_date' && otherDateValue && value < otherDateValue) {
      toast.error('Bitiş tarihi, başlangıç tarihinden önce olamaz!');
      return;
    }

    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user fixes the date
    if (editErrors[name]) {
      setEditErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle category toggle in edit mode
  const handleEditCategoryToggle = (category) => {
    // Don't allow category selection for consulting projects
    if (editFormData.project_id.startsWith('consulting_')) {
      return;
    }

    const isSelected = editSelectedCategories.some(cat => cat.id === category.id);
    
    if (isSelected) {
      // If already selected, remove selection
      setEditSelectedCategories([]);
      setEditFormData(prev => ({
        ...prev,
        category_id: []
      }));
    } else {
      // When a new category is selected, clear previous selections and add only the new one
      setEditSelectedCategories([category]);
      setEditFormData(prev => ({
        ...prev,
        category_id: [category.id]
      }));
    }
  };

  // Open edit modal with task data
  const openEditModal = async (task) => {
    try {
      setSelectedTask(task);
      
      // Parse assigned members data
      let members = [];
      if (task.assigned_members) {
        // If assigned_members is a string, parse it
        if (typeof task.assigned_members === 'string') {
          members = JSON.parse(task.assigned_members);
        } else {
          members = task.assigned_members;
        }

        // Fetch complete user profiles for assigned members
        const { data: userProfiles, error: userError } = await supabase
          .from('user_profiles')
          .select('*')
          .in('id', members.map(m => m.id));

        if (userError) throw userError;

        // Update members with complete user data
        members = userProfiles.map(profile => ({
          ...profile,
          profile_photo_url: profile.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.first_name + ' ' + profile.last_name)}&background=3b82f6&color=fff`
        }));
      }

      if (task.task_categories) {
        setEditSelectedCategories(task.task_categories.map(tc => tc.category));
      }
      
      const initialFormData = {
        id: task.id,
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        start_date: task.start_date || '',
        end_date: task.end_date || '',
        notes: task.notes || '',
        category_id: task.task_categories ? task.task_categories.map(tc => tc.category.id) : [],
        project_id: '',
        sub_project_id: '',
        consulting_id: '',
        consulting_project_id: ''
      };

      if (task.consulting_parent_id) {
        initialFormData.project_id = 'consulting';
        initialFormData.consulting_id = task.consulting_parent_id || '';
        initialFormData.consulting_project_id = task.consulting_project_id || '';
        
        await fetchConsultingFirms();
        if (task.consulting_parent_id) {
          await fetchConsultingProjects(task.consulting_parent_id);
        }
      } else {
        initialFormData.project_id = task.project_id || '';
        initialFormData.sub_project_id = task.sub_project_id || '';
        if (task.project_id) {
          await fetchSubProjects(task.project_id);
        }
      }

      await fetchTeamMembers();
      setMemberSearchQuery('');
      setEditSelectedMembers(members);
      setTempSelectedMembers(members); // Initialize temporary selection with current members
      setEditFiles(task.attachments || []);
      setEditFormData(initialFormData);
      setIsEditModalOpen(true);
    } catch (error) {
      console.error('Error opening edit modal:', error);
      toast.error('Düzenleme modalı açılırken bir hata oluştu');
    }
  };
  
  

  // Add this new function for handling file uploads
  const uploadFiles = async (taskId, files) => {
    const uploadedFiles = [];
    
    // Dosya işlemleri için ayrı Supabase client oluştur
    const fileSupabase = createClient(
      import.meta.env.VITE_FILE_SUPABASE_URL,
      import.meta.env.VITE_FILE_SUPABASE_ANON_KEY
    );
    
    const bucketName = import.meta.env.VITE_FILE_TASKS_SUPABASE_BUCKET_NAME;

    // Türkçe karakterleri ve özel karakterleri temizleme fonksiyonu
    const sanitizeFileName = (fileName) => {
      // Türkçe karakterleri değiştir
      const turkishChars = { 
        'ç': 'c', 'Ç': 'C', 
        'ğ': 'g', 'Ğ': 'G', 
        'ı': 'i', 'İ': 'I', 
        'ö': 'o', 'Ö': 'O', 
        'ş': 's', 'Ş': 'S', 
        'ü': 'u', 'Ü': 'U'
      };
      
      let sanitized = fileName;
      
      // Türkçe karakterleri değiştir
      Object.keys(turkishChars).forEach(char => {
        sanitized = sanitized.replace(new RegExp(char, 'g'), turkishChars[char]);
      });
      
      // Özel karakterleri kaldır (dosya sistemine zarar verebilecek karakterler)
      sanitized = sanitized.replace(/[*?:"<>|\\\/]/g, '');
      
      // Boşlukları alt çizgi ile değiştir
      sanitized = sanitized.replace(/\s+/g, '_');
      
      // Dosya adını küçük harfe çevir
      sanitized = sanitized.toLowerCase();
      
      // Timestamp ekle
      const timestamp = new Date().getTime();
      const finalName = `${timestamp}_${sanitized}`;
      
      return finalName;
    };

    for (const file of files) {
      // Skip if file already has a URL (means it's already uploaded)
      if (file.url) {
        uploadedFiles.push(file);
        continue;
      }

      try {
        // Dosya boyutu kontrolü (10MB)
        if (file.size > 128 * 1024 * 1024 * 1024) {
          toast.error(`${file.name} dosyası çok büyük. Maksimum dosya boyutu 128GB olmalıdır.`);
          continue;
        }

        const fileExt = file.name.split('.').pop();
        const baseName = file.name.substring(0, file.name.lastIndexOf('.'));
        const sanitizedBaseName = sanitizeFileName(baseName);
        const fileName = `${taskId}/${sanitizedBaseName}.${fileExt}`;

        // Upload progress tracking
        const options = {
          onUploadProgress: (progress) => {
            const percent = Math.round((progress.loaded * 100) / progress.total);
            // You can use this to update a progress bar if needed
          }
        };

        // fileSupabase kullanarak yükleme işlemi yap
        const { error: uploadError } = await fileSupabase.storage
          .from(bucketName)
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
            ...options
          });

        if (uploadError) {
          throw uploadError;
        }

        // Get public URL for the uploaded file
        const { data: publicUrl } = fileSupabase.storage
          .from(bucketName)
          .getPublicUrl(fileName);

        uploadedFiles.push({
          name: file.name,
          size: file.size,
          type: file.type,
          url: publicUrl.publicUrl,
          path: fileName,
          uploaded_at: new Date().toISOString()
        });

        toast.success(`${file.name} başarıyla yüklendi.`);

      } catch (error) {
        console.error('Error uploading file:', error);
        toast.error(`${file.name} yüklenirken hata oluştu: ${error.message || error}`);
      }
    }

    return uploadedFiles;
  };

  

  // Update handleUpdateTask function
  const handleUpdateTask = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      // Save original task for notification
      const originalTask = { ...selectedTask };
      
      // Apply temporary member selections to the actual state
      setEditSelectedMembers(tempSelectedMembers);
      
      // Önce yeni dosyaları yükle
      let updatedAttachments = [...(editFiles.filter(file => file.url) || [])];
      
      // Yeni dosyaları kontrol et ve yükle
      const newFiles = editFiles.filter(file => !file.url);
      if (newFiles.length > 0) {
        const uploadedFiles = await uploadFiles(selectedTask.id, newFiles);
        updatedAttachments = [...updatedAttachments, ...uploadedFiles];
      }
      
      // Prepare task data
      const taskData = {
        title: editFormData.title,
        description: editFormData.description,
        priority: editFormData.priority,
        start_date: editFormData.start_date,
        end_date: editFormData.end_date,
        notes: editFormData.notes,
        assigned_members: tempSelectedMembers.map(member => ({
          id: member.id,
          name: `${member.first_name} ${member.last_name}`,
          avatar: member.profile_photo_url,
        })),
        updated_at: new Date().toISOString(),
        attachments: updatedAttachments, // Yüklenen dosyaları ekle
      };

      // Handle project selection
      if (editFormData.project_id === 'consulting') {
        taskData.project_id = null;
        taskData.sub_project_id = null;
        taskData.consulting_project_id = editFormData.consulting_project_id;
        taskData.consulting_parent_id = editFormData.consulting_id;
      } else {
        taskData.project_id = editFormData.project_id;
        taskData.sub_project_id = editFormData.sub_project_id || null;
        taskData.consulting_project_id = null;
        taskData.consulting_parent_id = null;
      }

      // Update task
      const { error: taskError } = await supabase
        .from('tasks')
        .update(taskData)
        .eq('id', selectedTask.id);

      if (taskError) throw taskError;

      // Handle categories if not consulting
      if (editFormData.project_id !== 'consulting' && editSelectedCategories.length > 0) {
        // First delete existing categories
        const { error: deleteError } = await supabase
          .from('task_categories')
          .delete()
          .eq('task_id', selectedTask.id);

        if (deleteError) throw deleteError;

        // Then insert new categories
        const { error: categoryError } = await supabase
          .from('task_categories')
          .insert(
            editSelectedCategories.map(category => ({
              task_id: selectedTask.id,
              category_id: category.id
            }))
          );

        if (categoryError) throw categoryError;
      }

      // Determine what fields have changed for the activity record
      const changedFields = [];
      if (originalTask.title !== taskData.title) changedFields.push('Görev Başlığı');
      if (originalTask.description !== taskData.description) changedFields.push('Açıklama Metni');
      if (originalTask.priority !== taskData.priority) changedFields.push('Öncelik Durumu');
      if (originalTask.start_date !== taskData.start_date || originalTask.end_date !== taskData.end_date) changedFields.push('Proje Tarihleri');
      if (originalTask.notes !== taskData.notes) changedFields.push('Notlar');
      
      // Dosya değişikliklerini kontrol et
      const originalFiles = originalTask.attachments || [];
      if (JSON.stringify(originalFiles) !== JSON.stringify(updatedAttachments)) {
        changedFields.push('Ekler');
      }
      
      // Check for assigned members changes
      const originalMembers = originalTask.assigned_members || [];
      const newMembers = taskData.assigned_members || [];
      
      const originalMemberIds = new Set(
        (typeof originalMembers === 'string' ? JSON.parse(originalMembers) : originalMembers)
          .map(member => member.id)
      );
      
      const newMemberIds = new Set(newMembers.map(member => member.id));
      
      if (originalMemberIds.size !== newMemberIds.size || 
          [...newMemberIds].some(id => !originalMemberIds.has(id)) ||
          [...originalMemberIds].some(id => !newMemberIds.has(id))) {
        changedFields.push('Atanan Üyeler');
      }
      
      // Create activity description
      let activityDescription = `${taskData.title} görevinde güncelleme yapıldı.`;
      
      if (changedFields.length > 0) {
        activityDescription = `${taskData.title} görevinde değişiklikler yapıldı:\n\n`;
        changedFields.forEach(field => {
          activityDescription += `• ${field} güncellendi\n`;
        });
      }
      
      // Create activity record
      if (currentUser) {
        const { error: activityError } = await supabase.from('activities').insert({
          user_id: currentUser.id,
          activity_type: 'update',
          description: activityDescription,
          task_id: selectedTask.id
        });
        
        if (activityError) {
          console.error('Error creating activity:', activityError);
        }
      }

      // Send notification about task update
      if (currentUser) {
        const updatedTask = {
          ...selectedTask,
          ...taskData,
          categories: editSelectedCategories, // Kategorileri burada updatedTask'e ekle
          id: selectedTask.id
        };
        
        await notifyTaskUpdated(
          updatedTask,
          currentUser.id,
          `${currentUser.first_name} ${currentUser.last_name}`,
          originalTask
        );
      }

      // Success handling
      toast.success('Görev başarıyla güncellendi');
      setIsEditModalOpen(false);
      
      // Update cache immediately with the updated task
      const updatedTask = {
        ...selectedTask,
        ...taskData,
        categories: editSelectedCategories,
        id: selectedTask.id
      };
      updateCachedTask(updatedTask);
      
      // Refresh task details and update local state
      await fetchTaskDetails(selectedTask.id);
      
      // Update local tasks state
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === selectedTask.id ? updatedTask : task
        )
      );

    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Görev güncellenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Alt projeleri getir
  const fetchSubProjects = async (projectId) => {
    try {
      // Eğer consulting değeri ise, sorgu yapmayı atla
      if (projectId === 'consulting') {
        // console.log('Consulting projesi seçildi, alt projeler sorgulanmayacak');
        return;
      }

      const { data, error } = await supabase
        .from('sub_projects')
        .select('*')
        .eq('parent_project_id', projectId);
        
      if (error) {
        console.error('Error fetching sub-projects:', error);
        return;
      }
      
      setSubProjects(prev => ({
        ...prev,
        [projectId]: data
      }));
    } catch (error) {
      console.error('Error fetching sub-projects:', error);
    }
  };

  // Edit modal açıldığında alt projeleri getir
  useEffect(() => {
    if (isEditModalOpen && editFormData.project_id) {
      fetchSubProjects(editFormData.project_id);
    }
  }, [isEditModalOpen, editFormData.project_id]);

  // Add file handling functions for edit mode
  const handleEditFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setEditFiles([...editFiles, ...newFiles]);
  };

  // Add this new function after the fetchTeamMembers function
  const deleteFileFromStorage = async (fileUrl) => {
    try {
      // Dosya işlemleri için ayrı Supabase client oluştur
      const fileSupabase = createClient(
        import.meta.env.VITE_FILE_SUPABASE_URL,
        import.meta.env.VITE_FILE_SUPABASE_ANON_KEY
      );
      
      const bucketName = import.meta.env.VITE_FILE_TASKS_SUPABASE_BUCKET_NAME;
      
      // Extract full file path from URL
      // URL formatı: https://xxxx.supabase.co/storage/v1/object/public/bucket-name/task_id/filename
      const urlParts = fileUrl.split('/');
      
      // Dosya adı ve görev ID'sini doğru şekilde çıkar
      // URL'in son iki kısmı: "task_id/filename" şeklinde olmalı
      const taskIdAndFileName = urlParts.slice(-2).join('/');
      
      // console.log('Silinecek dosya yolu:', taskIdAndFileName);
      
      if (!taskIdAndFileName) {
        throw new Error('Dosya yolu bulunamadı');
      }

      // Delete file from storage
      const { error: storageError } = await fileSupabase.storage
        .from(bucketName)
        .remove([taskIdAndFileName]);

      if (storageError) {
        console.error('Dosya silinirken hata:', storageError);
        throw storageError;
      }

      // console.log('Dosya başarıyla silindi:', taskIdAndFileName);
      return true;
    } catch (error) {
      console.error('Error deleting file from storage:', error);
      throw error;
    }
  };

  // Update the removeEditFile function
  const removeEditFile = async (index, hasUrl = false) => {
    try {
      setIsLoading(true);
      
      const filesArray = [...editFiles];
      const removedFile = filesArray[index];
      
      if (hasUrl && removedFile.url) {
        // Silinecek dosya bilgileri
        // console.log('Silinen dosya:', removedFile);
        
        // Dosya yolu bilgisini kontrol et
        if (removedFile.path) {
          // Dosya zaten path bilgisi içeriyorsa, direkt path'i kullan
          // console.log('Silinecek dosya yolu:', removedFile.path);
          
          // Dosya işlemleri için ayrı Supabase client oluştur
          const fileSupabase = createClient(
            import.meta.env.VITE_FILE_SUPABASE_URL,
            import.meta.env.VITE_FILE_SUPABASE_ANON_KEY
          );
          
          // Dosyayı doğrudan path bilgisi ile sil
          const { error: storageError } = await fileSupabase.storage
            .from(import.meta.env.VITE_FILE_TASKS_SUPABASE_BUCKET_NAME)
            .remove([removedFile.path]);
          
          if (storageError) {
            console.error('Dosya silinirken hata:', storageError);
            throw storageError;
          }
          
          // console.log('Dosya başarıyla silindi:', removedFile.path);
        } else {
          // Path bilgisi yoksa URL'den çıkarmaya çalış
          await deleteFileFromStorage(removedFile.url);
        }
        
        // Dosyayı veritabanından da kaldır (eğer task güncellenirse)
        if (selectedTask && selectedTask.id) {
          // Eğer attachments dizisi varsa güncelle
          if (selectedTask.attachments && Array.isArray(selectedTask.attachments)) {
            const updatedAttachments = selectedTask.attachments.filter(
              file => file.url !== removedFile.url
            );
            
            // Seçili görevin dosya listesini bellekte güncelle
            setSelectedTask(prevTask => ({
              ...prevTask,
              attachments: updatedAttachments
            }));
            
            // Veritabanında güncelleme yap
            const { error: updateError } = await supabase
              .from('tasks')
              .update({ attachments: updatedAttachments })
              .eq('id', selectedTask.id);
            
            if (updateError) {
              console.error('Görev dosya bilgileri güncellenirken hata:', updateError);
            } else {
              // console.log('Görev dosya bilgileri güncellendi');
            }
          }
        }
      }
      
      // Remove from editFiles array
      filesArray.splice(index, 1);
      setEditFiles(filesArray);
      
      toast.success('Dosya başarıyla kaldırıldı');
    } catch (error) {
      console.error('Error removing file:', error);
      toast.error(`Dosya kaldırma hatası: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Keep only the memoized implementation
  const filteredTeamMembers = useMemo(() => {
    // Eğer arama kutusu boşsa, tüm üyeleri göster
    if (!memberSearchQuery.trim()) {
      return teamMembers;
    }
    
    // Arama kutusu doluysa, arama kriterlerine göre filtrele
    return teamMembers.filter((member) => {
      const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
      const jobPosition = member.job_position?.toLowerCase() || '';
      const email = member.email?.toLowerCase() || '';
      const searchTerm = memberSearchQuery.toLowerCase().trim();
      
      return (
        fullName.includes(searchTerm) ||
        jobPosition.includes(searchTerm) ||
        email.includes(searchTerm)
      );
    });
  }, [teamMembers, memberSearchQuery]);

  // Single definition of getFilteredMembers
  const getFilteredMembers = () => {
    return availableMembers.filter(member =>
      `${member.first_name} ${member.last_name}`.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
      member.job_position?.toLowerCase().includes(memberSearchQuery.toLowerCase())
    );
  };

  // Add getDefaultAvatar helper function
  const getDefaultAvatar = (firstName, lastName) => {
    const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=3b82f6&color=fff`;
  };

  // Kullanıcıları getir
  const fetchTeamMembers = async () => {
    try {
      const { data: members, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('first_name', { ascending: true });

      if (error) throw error;

      setTeamMembers(members);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Kullanıcılar yüklenirken bir hata oluştu');
    }
  };

  // Add this new function for deleting a task
  const handleDeleteTask = async () => {
    try {
      setIsDeleting(true);

      // Önce bildirim gönder (görev silinmeden önce)
      await notifyTaskDeleted(
        selectedTask,
        currentUser.id,
        `${currentUser.first_name} ${currentUser.last_name}`
      );

      // Göreve ait dosyaları sil
      if (selectedTask.attachments && selectedTask.attachments.length > 0) {
        try {
          // console.log(`Görev ID ${selectedTask.id} için dosyalar siliniyor...`);
          
          // Dosya işlemleri için ayrı Supabase client oluştur
          const fileSupabase = createClient(
            import.meta.env.VITE_FILE_SUPABASE_URL,
            import.meta.env.VITE_FILE_SUPABASE_ANON_KEY
          );
          
          // console.log(`Bucket URL: ${import.meta.env.VITE_FILE_SUPABASE_URL}`);
          // console.log(`Bucket name: ${import.meta.env.VITE_FILE_TASKS_SUPABASE_BUCKET_NAME}`);
          
          // Klasör yolunu oluştur ve sil
          const folderPath = `${selectedTask.id}`;
          // console.log(`Silinecek klasör: ${folderPath}`);
          
          // Önce klasör içeriğini listele
          const { data: folderContents, error: listError } = await fileSupabase.storage
            .from(import.meta.env.VITE_FILE_TASKS_SUPABASE_BUCKET_NAME)
            .list(folderPath);
            
          if (listError) {
            console.error(`Klasör içeriği listelenirken hata: ${listError.message}`);
          } else {
            // console.log(`Klasör içeriği (${folderContents ? folderContents.length : 0} dosya):`, folderContents);
            
            if (folderContents && folderContents.length > 0) {
              // Tüm dosya yollarını oluştur
              const filePaths = folderContents.map(file => `${folderPath}/${file.name}`);
              // console.log(`Silinecek dosya yolları:`, filePaths);
              
              // Tüm dosyaları sil
              const { data: deleteData, error: deleteError } = await fileSupabase.storage
                .from(import.meta.env.VITE_FILE_TASKS_SUPABASE_BUCKET_NAME)
                .remove(filePaths);
                
              if (deleteError) {
                console.error(`Dosyalar silinirken hata: ${deleteError.message}`);
              } else {
                // console.log(`Dosyalar başarıyla silindi!`, deleteData);
              }
            } else {
              // console.log(`Klasörde silinecek dosya bulunamadı.`);
            }
          }
        } catch (fileError) {
          console.error('Dosya silme işlemi sırasında hata:', fileError);
          // Dosya silme hatası görev silme işlemini engellemesin
        }
      }

      // Görev notlarını ve ilişkili dosyaları sil
      try {
        // Önce task_notes tablosundan görevin notlarına ait dosya yollarını al
        const { data: taskNotes, error: notesError } = await supabase
          .from('task_notes')
          .select('id, attachment_url')
          .eq('task_id', selectedTask.id);
        
        if (notesError) {
          console.error('Görev notları alınırken hata oluştu:', notesError);
        } else if (taskNotes && taskNotes.length > 0) {
          // Storage'dan dosyaları sil
          const attachmentPaths = taskNotes
            .filter(note => note.attachment_url)
            .map(note => note.attachment_url);
          
          if (attachmentPaths.length > 0) {
            // Storage dosyalarını sil
            const { error: storageError } = await supabase.storage
              .from('task_attachments')
              .remove(attachmentPaths);
            
            if (storageError) {
              console.error('Not dosyaları silinirken hata oluştu:', storageError);
            }
          }
          
          // task_notes tablosundan görev notlarını sil
          const { error: deleteNotesError } = await supabase
            .from('task_notes')
            .delete()
            .eq('task_id', selectedTask.id);
          
          if (deleteNotesError) {
            console.error('Görev notları silinirken hata oluştu:', deleteNotesError);
          } else {
            // console.log(`${taskNotes.length} adet görev notu başarıyla silindi`);
          }
        }
      } catch (notesError) {
        console.error('Görev notlarını silme işlemi sırasında hata:', notesError);
        // Not silme hatası görev silme işlemini engellemesin
      }

      // İlgili aktiviteleri sil
      const { error: activitiesError } = await supabase
        .from('activities')
        .delete()
        .eq('task_id', selectedTask.id);

      if (activitiesError) throw activitiesError;

      // Göreve atanan kategorileri sil
      const { error: categoriesError } = await supabase
        .from('task_categories')
        .delete()
        .eq('task_id', selectedTask.id);

      if (categoriesError) {
        console.error('Kategoriler silinirken hata oluştu:', categoriesError);
      }

      // Görevi sil
      const { error: taskError } = await supabase
        .from('tasks')
        .delete()
        .eq('id', selectedTask.id);

      if (taskError) throw taskError;

      // Cache'den görevi kaldır
      removeCachedTask(selectedTask.id);
      
      // UI'ı güncelle
      setTasks(prevTasks => prevTasks.filter(task => task.id !== selectedTask.id));
      setSelectedTask(null);
      setIsDeleteModalOpen(false);
      setIsTaskModalOpen(false);
      toast.success('Görev başarıyla silindi');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Görev silinirken bir hata oluştu');
    } finally {
      setIsDeleting(false);
    }
  };

  // URL'den task parametresini kontrol et
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (taskId) {
      fetchTaskDetails(taskId).then(() => {
        setIsTaskModalOpen(true);
        // URL'den task parametresini temizle
        navigate('/tasks', { replace: true });
      });
    }
  }, [searchParams, navigate]);

  // URL'den project parametresini kontrol et ve filtrele
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (projectId) {
      setSelectedProject(projectId);
      // URL'den project parametresini temizle (isteğe bağlı - kullanıcı deneyimini iyileştirir)
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('project');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [searchParams]);

  // Task modal açıkken body scroll'u engelle
  useEffect(() => {
    if (isTaskModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isTaskModalOpen]);

  // Danışmanlık firmalarını getir
  const fetchConsultingFirms = async () => {
    try {
      const { data, error } = await supabase
        .from('consulting')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setConsultingFirms(data || []);
    } catch (error) {
      console.error('Error fetching consulting firms:', error);
      toast.error('Danışmanlık firmaları yüklenirken bir hata oluştu');
    }
  };

  // Seçilen firmanın projelerini getir
  const fetchConsultingProjects = async (firmId) => {
    try {
      const { data, error } = await supabase
        .from('consulting_sub_projects')
        .select(`
          id,
          name,
          description,
          status,
          team_size,
          task_count,
          completed_tasks,
          start_date,
          end_date
        `)
        .eq('parent_consulting_id', firmId);
      
      if (error) throw error;
      setConsultingProjects(prev => ({
        ...prev,
        [firmId]: data || []
      }));
    } catch (error) {
      console.error('Error fetching consulting projects:', error);
      toast.error('Danışmanlık projeleri yüklenirken bir hata oluştu');
    }
  };

  // Add CSS animations for card effects
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { 
          opacity: 0; 
          transform: scale(0.98);
        }
        to { 
          opacity: 1; 
          transform: scale(1);
        }
      }
      
      @keyframes subtleGlow {
        0% { opacity: 0; }
        50% { opacity: 0.5; }
        100% { opacity: 0.3; }
      }
      
      .task-card-hover {
        transform: translateY(-1px);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Proje adını getiren yardımcı fonksiyon
  const getProjectName = (projectId) => {
    if (projectId === 'all') return 'Tüm Projeler';
    if (projectId === 'consulting') return 'Danışmanlık';
    
    // Consulting firm kontrolü
    if (projectId.startsWith('consulting_firm_')) {
      const firmId = projectId.replace('consulting_firm_', '');
      const firm = consultingFirms.find(f => f.id === firmId);
      return firm ? `${firm.name} (Danışmanlık)` : 'Bilinmeyen Danışmanlık';
    }
    
    // Consulting project kontrolü
    if (projectId.startsWith('consulting_project_')) {
      const projectIdNumber = projectId.replace('consulting_project_', '');
      const consultingProject = Object.values(consultingProjects).flat().find(p => p.id === parseInt(projectIdNumber));
      return consultingProject ? `${consultingProject.name} (Danışmanlık Projesi)` : 'Bilinmeyen Danışmanlık Projesi';
    }
    
    // Normal proje kontrolü
    const normalProject = projects.find(p => p.id === projectId);
    if (normalProject) {
      return normalProject.name;
    }
    
    // Alt proje kontrolü - subProjects artık object olduğu için Object.values ile array'e çeviriyoruz
    const allSubProjects = Object.values(subProjects).flat();
    const subProject = allSubProjects.find(sp => sp.id === projectId);
    if (subProject) {
      const parentProject = projects.find(p => p.id === subProject.parent_project_id);
      return parentProject ? `${subProject.name} (${parentProject.name})` : subProject.name;
    }
    
    return 'Bilinmeyen Proje';
  };

  // Sayfa her yüklendiğinde/yenilendiğinde status güncelleme işlemi
  useEffect(() => {
    const updateTasksStatus = async () => {
      try {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); // Günün başlangıcına ayarla

        // 1. Henüz başlamamış görevleri bul (start_date bugünden sonra olan görevler) - "todo" yap
        const { data: pendingTasks, error: fetchPendingError } = await supabase
          .from('tasks')
          .select('id, start_date, status')
          .gt('start_date', currentDate.toISOString())
          .neq('status', 'todo'); // Zaten todo olmayanlar

        if (fetchPendingError) {
          console.error('Bekleyen görevler getirilirken hata:', fetchPendingError);
        } else if (pendingTasks && pendingTasks.length > 0) {
          const { error: updatePendingError } = await supabase
            .from('tasks')
            .update({ status: 'todo' })
            .in('id', pendingTasks.map(task => task.id));

          if (updatePendingError) {
            console.error('Bekleyen görev durumları güncellenirken hata:', updatePendingError);
          } else {
            // console.log(`${pendingTasks.length} görevi "todo" durumuna güncellendi`);
          }
        }

        // 2. Devam eden görevleri bul (start_date <= bugün <= end_date) - "in_progress" yap
        const { data: activeTasks, error: fetchActiveError } = await supabase
          .from('tasks')
          .select('id, start_date, end_date, status')
          .lte('start_date', currentDate.toISOString())
          .gte('end_date', currentDate.toISOString())
          .neq('status', 'in_progress') // Zaten in_progress olmayanlar
          .neq('status', 'completed'); // Tamamlanmış olanları dışla

        if (fetchActiveError) {
          console.error('Aktif görevler getirilirken hata:', fetchActiveError);
        } else if (activeTasks && activeTasks.length > 0) {
          const { error: updateActiveError } = await supabase
            .from('tasks')
            .update({ status: 'in_progress' })
            .in('id', activeTasks.map(task => task.id));

          if (updateActiveError) {
            console.error('Aktif görev durumları güncellenirken hata:', updateActiveError);
          } else {
            // console.log(`${activeTasks.length} görevi "in_progress" durumuna güncellendi`);
          }
        }

        // Toplam sonuç
        const totalPending = pendingTasks?.length || 0;
        const totalActive = activeTasks?.length || 0;
        if (totalPending === 0 && totalActive === 0) {
          // console.log('Güncellenecek görev bulunamadı');
        }

      } catch (error) {
        console.error('Görev durumları güncellenirken beklenmeyen hata:', error);
      }
    };

    updateTasksStatus();
  }, []); // Sadece component mount olduğunda çalış

  // URL'den görev ID'sini kontrol et ve modalı aç
  useEffect(() => {
    const taskIdFromUrl = searchParams.get('taskId');
    if (taskIdFromUrl) {
      setIsTaskModalOpen(true);
      fetchTaskDetails(taskIdFromUrl);
    }
  }, [searchParams]); // searchParams değiştiğinde tekrar çalıştır

  return (
    
    <div className="max-w-7xl mx-auto ">
        <div className="flex justify-between items-center mt-2 mb-12">
          {/* Geri Dön button */}
          <button
                type="button"
                onClick={() => window.history.back()}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Geri Dön</span>
          </button>
          
          {/* Permission level indicator */}
          {userRole && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              userRole === 'super' 
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
                : userRole === 'admin' 
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {userRole === 'super' ? (
                <Shield className="w-4 h-4" />
              ) : userRole === 'admin' ? (
                <User className="w-4 h-4" />
              ) : (
                <Users className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">
                {userRole === 'super' 
                  ? 'Süper Yönetici' 
                  : userRole === 'admin' 
                    ? 'Yönetici' 
                    : 'Standart Kullanıcı'
                }
              </span>
            </div>
          )}
        </div>


        



      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        {/* Başlık ve Araçlar */}
        
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Görevler
              </h2>
              
              {userRole === 'admin' || userRole === 'super' ? (
                <button
                  type="button"
                  onClick={() => navigate('/tasks/create')}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Yeni Görev
                </button>
              ) : null}

              
            </div>
            
            <div className="flex items-center gap-2">
              {/* Super user filter toggle */}
              {userRole === 'super' && (
                <div className="relative mr-2">
                  <button
                    type="button"
                    onClick={() => setShowOnlyMyTasks(!showOnlyMyTasks)}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      showOnlyMyTasks
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                    }`}
                  >
                    {showOnlyMyTasks ? (
                      <>
                        <Users className="w-4 h-4" />
                        Görevlerim
                      </>
                    ) : (
                      <>
                        <FolderKanban className="w-4 h-4" />
                        Tüm Görevler
                      </>
                    )}
                  </button>
                </div>
              )}
              
              <div className="relative">
                <input
                  type="text"
                  placeholder="Görev ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              <div className="flex items-center gap-2 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                {VIEW_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.id}
                      onClick={() => setViewMode(option.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === option.id
                          ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                      title={`${option.label} (Tercihiniz hatırlanır)`}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
              
              {/* Detailed View Toggle */}
              {viewMode === 'grid' && (
                <button
                  onClick={() => setDetailedView(!detailedView)}
                  className={`p-2 rounded-lg transition-colors ${
                    detailedView
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title={`${detailedView ? 'Normal Görünüm' : 'Detaylı Görünüm'} (Tercih hatırlanır)`}
                >
                  <Info className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${
                  showFilters
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Filter className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Enhanced Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 overflow-hidden"
              >
                <div className="p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Sol Kolon: Proje Seçimi */}
                    <div className="space-y-6">
                      {/* Proje Filtresi */}
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <FolderKanban className="w-4 h-4" />
                          Proje
                        </label>
                        <select
                          value={selectedProject}
                          onChange={(e) => setSelectedProject(e.target.value)}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all hover:border-primary-500 dark:hover:border-primary-500"
                        >
                          <option value="all">Tüm Projeler</option>
                          
                          {/* Projeler ve Alt Projeleri (Hiyerarşik Yapı) */}
                          {projects.map(project => (
                            <React.Fragment key={`project_group_${project.id}`}>
                              {/* Ana Proje */}
                              <option value={project.id}>
                                {project.name === 'Bakım' ? '📋 ' : ''}{project.name}
                              </option>
                              
                              {/* Bu projenin alt projeleri */}
                              {subProjects[project.id]?.map(subProject => (
                                <option key={`sub_${subProject.id}`} value={subProject.id}>
                                  &nbsp;&nbsp;&nbsp;&nbsp;📂 {subProject.name}
                                </option>
                              ))}
                            </React.Fragment>
                          ))}
                          
                          {/* Danışmanlık */}
                          <optgroup label="🏢 Danışmanlık">
                            <option value="consulting">Tüm Danışmanlık Projeleri</option>
                            
                            {/* Danışmanlık Firmaları */}
                            {consultingFirms.map(firm => (
                              <option key={`consulting_firm_${firm.id}`} value={`consulting_firm_${firm.id}`}>
                                📢 {firm.name}
                              </option>
                            ))}
                            
                            {/* Danışmanlık Alt Projeleri */}
                            {Object.entries(consultingProjects).map(([firmId, projects]) => 
                              projects.map(project => (
                                <option key={`consulting_project_${project.id}`} value={`consulting_project_${project.id}`}>
                                  📄 {consultingFirms.find(f => f.id === firmId)?.name} → {project.name}
                                </option>
                              ))
                            )}
                          </optgroup>
                        </select>
                      </div>

                      {/* Tarih Aralığı */}
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Calendar className="w-4 h-4" />
                          Tarih Aralığı
                        </label>
                        <div className="space-y-4">
                          <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Başlangıç Tarihi</label>
                            <input
                              type="date"
                              value={dateRangeFilter.start}
                              onChange={(e) => setDateRangeFilter(prev => ({ ...prev, start: e.target.value }))}
                              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all hover:border-primary-500 dark:hover:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Bitiş Tarihi</label>
                            <input
                              type="date"
                              value={dateRangeFilter.end}
                              onChange={(e) => setDateRangeFilter(prev => ({ ...prev, end: e.target.value }))}
                              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all hover:border-primary-500 dark:hover:border-primary-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sağ Kolon: Durum ve Öncelik */}
                    <div className="space-y-6">
                      {/* Durum Filtresi */}
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Clock className="w-4 h-4" />
                          Durum
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {TASK_STATUS.map((status) => (
                            <button
                              key={status.id}
                              onClick={() => setStatusFilter(status.id)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                                statusFilter === status.id
                                  ? `bg-${status.color}-100 dark:bg-${status.color}-900/30 text-${status.color}-700 dark:text-${status.color}-400 ring-2 ring-${status.color}-500 dark:ring-${status.color}-400`
                                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {status.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Öncelik Filtresi */}
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <AlertCircle className="w-4 h-4" />
                          Öncelik
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {TASK_PRIORITY.map((priority) => (
                            <button
                              key={priority.id}
                              onClick={() => setPriorityFilter(priority.id)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                                priorityFilter === priority.id
                                  ? `bg-${priority.color}-100 dark:bg-${priority.color}-900/30 text-${priority.color}-700 dark:text-${priority.color}-400 ring-2 ring-${priority.color}-500 dark:ring-${priority.color}-400`
                                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {priority.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Tamamlanma Durumu Toggle */}
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          <CheckCircle2 className="w-4 h-4" />
                          Tamamlanma Durumu
                        </label>
                        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                          <button
                            onClick={() => setCompletionFilter('not_completed')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                              completionFilter === 'not_completed'
                                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          >
                            Tamamlanmayanlar
                          </button>
                          <button
                            onClick={() => setCompletionFilter('all')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                              completionFilter === 'all'
                                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          >
                            Tümü
                          </button>
                          <button
                            onClick={() => setCompletionFilter('completed')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                              completionFilter === 'completed'
                                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          >
                            Tamamlananlar
                          </button>
                        </div>
                      </div>

                      {/* Rapor Durumu Filtresi */}
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <FileText className="w-4 h-4" />
                          Rapor Durumu
                        </label>
                        <select
                          id="filter-notes"
                          name="filter-notes"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          value={hasNotesFilter}
                          onChange={(e) => setHasNotesFilter(e.target.value)}
                        >
                          <option value="all">Tüm Görevler</option>
                          <option value="has_notes">Raporu Olan Görevler</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Alt Kısım: Filtreleri Sıfırla ve Aktif Filtreler */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {/* Aktif Filtreler */}
                      {statusFilter !== 'all' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                          Durum: {TASK_STATUS.find(s => s.id === statusFilter)?.label}
                          <button onClick={() => setStatusFilter('all')} className="hover:text-blue-900">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      {priorityFilter !== 'all' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-full">
                          Öncelik: {TASK_PRIORITY.find(p => p.id === priorityFilter)?.label}
                          <button onClick={() => setPriorityFilter('all')} className="hover:text-purple-900">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      {completionFilter !== 'all' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                          {completionFilter === 'completed' ? 'Tamamlananlar' : 'Tamamlanmayanlar'}
                          <button onClick={() => setCompletionFilter('all')} className="hover:text-green-900">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      {selectedProject !== 'all' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                          Proje: {getProjectName(selectedProject)}
                          <button onClick={() => setSelectedProject('all')} className="hover:text-green-900">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      {(dateRangeFilter.start || dateRangeFilter.end) && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded-full">
                          Tarih Filtresi
                          <button onClick={() => setDateRangeFilter({ start: '', end: '' })} className="hover:text-orange-900">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                    </div>

                    {/* Cache Status */}
                    {cacheStats && (
                      <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${cacheStats.isValid ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {cacheStats.isValid 
                                ? `Cache aktif (${cacheStats.taskCount} görev, ${cacheStats.cacheAge}dk önce güncellendi)`
                                : 'Cache geçersiz - yeniden yükleniyor'
                              }
                            </span>
                          </div>
                          <button
                            onClick={() => fetchTasks(true)}
                            disabled={isRefreshing}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                          >
                            <RotateCcw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                            Yenile
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Filtreleri Sıfırla */}
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setPriorityFilter('all');
                        setSelectedProject('all');
                        setDateRangeFilter({ start: '', end: '' });
                        setCompletionFilter('all');
                        setSortOption('start_date_asc');
                        setSearchQuery('');
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Filtreleri Sıfırla
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Sıralama Seçenekleri - Kartların Hemen Üstünde */}
          <div className="flex items-center justify-between mt-6 mb-4 p-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {filteredTasks.length} görev
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                    bulundu
                  </span>
                </div>
              </div>
              
              {searchQuery && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    "{searchQuery}" için sonuçlar
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <div className="p-1.5 bg-gray-100 dark:bg-gray-600 rounded-lg">
                  <ArrowUpDown className="w-4 h-4" />
                </div>
                <label className="text-sm font-medium">
                  Sıralama
                </label>
              </div>
              
              <div className="relative">
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="appearance-none px-4 py-2.5 pl-10 pr-8 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all hover:border-primary-400 dark:hover:border-primary-500 shadow-sm hover:shadow-md min-w-[220px] cursor-pointer"
                >
                  {SORT_OPTIONS.map(option => (
                    <option key={option.id} value={option.id} className="py-2">
                      {option.label}
                    </option>
                  ))}
                </select>
                
                {/* Custom dropdown icon */}
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  {(() => {
                    const currentSort = SORT_OPTIONS.find(opt => opt.id === sortOption);
                    if (currentSort?.id.includes('start_date')) return <Calendar className="w-4 h-4 text-green-500" />;
                    if (currentSort?.id.includes('remaining_time')) return <Clock className="w-4 h-4 text-orange-500" />;
                    if (currentSort?.id.includes('created')) return <Plus className="w-4 h-4 text-blue-500" />;
                    if (currentSort?.id.includes('due')) return <Calendar className="w-4 h-4 text-red-500" />;
                    if (currentSort?.id.includes('priority')) return <AlertCircle className="w-4 h-4 text-purple-500" />;
                    return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
                  })()}
                </div>
                
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              
              {/* Sort indicator */}
              <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                {(() => {
                  const isAscending = sortOption.includes('_asc');
                  return isAscending ? (
                    <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <ArrowUp className="w-3 h-3" />
                      <span className="text-xs font-medium">A→Z</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                      <ArrowDown className="w-3 h-3" />
                      <span className="text-xs font-medium">Z→A</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Görev Listesi */}
        <motion.div 
          className="p-6"
          initial={{ 
            height: 0,
            opacity: 0,
            scaleY: 0,
            transformOrigin: "top"
          }}
          animate={{ 
            height: "auto",
            opacity: 1,
            scaleY: 1,
            transformOrigin: "top"
          }}
          transition={{ 
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94],
            opacity: { duration: 0.6, delay: 0.2 },
            scaleY: { duration: 0.8, ease: "easeOut" }
          }}
          style={{ overflow: "hidden" }}
        >
          <motion.div 
            className={`${
              viewMode === 'grid' 
                ? detailedView 
                  ? 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 4xl:grid-cols-4 gap-6' 
                  : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-3 gap-4'
                : 'space-y-2'
            }`}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ 
              duration: 0.6, 
              delay: 0.4,
              ease: "easeOut"
            }}
          >
            {isLoading ? (
              // Yükleme durumu
              <div className={`${
                viewMode === 'grid' 
                  ? 'col-span-full'
                  : ''
              }`}>
                <TaskSkeletonGrid count={6} isDetailedView={detailedView} />
              </div>
            ) : filteredTasks.length > 0 ? (
              // Görevler
              <AnimatePresence>
                {filteredTasks.map((task, index) => (
                  <motion.div
                    key={task.id}
                    initial={{ 
                      opacity: 0, 
                      y: 30,
                      scale: 0.95
                    }}
                    animate={{ 
                      opacity: 1, 
                      y: 0,
                      scale: 1
                    }}
                    exit={{ 
                      opacity: 0, 
                      y: -20,
                      scale: 0.95
                    }}
                    transition={{ 
                      duration: 0.5,
                      delay: 0.6 + (index * 0.1), // Staggered delay
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                  >
                    {viewMode === 'grid' ? (
                      <TaskCard task={task} detailedView={detailedView} />
                    ) : (
                      <TaskListItem task={task} />
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            ) : (
              // Akıllı görev bulunamadı mesajı
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`${
                  viewMode === 'grid' 
                    ? 'col-span-full'
                    : ''
                }`}
              >
                <NoTasksMessage 
                  filteredTasks={filteredTasks}
                  allTasks={tasks}
                  searchQuery={searchQuery}
                  statusFilter={statusFilter}
                  priorityFilter={priorityFilter}
                  completionFilter={completionFilter}
                  selectedProject={selectedProject}
                  dateRangeFilter={dateRangeFilter}
                  hasNotesFilter={hasNotesFilter}
                />
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Görev Detay Modal */}
      <AnimatePresence>
        {isTaskModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsTaskModalOpen(false);
                setSelectedTask(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-6xl max-h-[85vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-[18px] shadow-xl flex flex-col md:flex-row overflow-hidden"
            >
              {isTaskDetailsLoading ? (
                // Loading animation when task details are being fetched
                <div className="flex-1 flex flex-col items-center justify-center p-20 overflow-hidden">
                  <div className="relative">
                    <motion.div 
                      animate={{ 
                        rotate: 360,
                        transition: { 
                          duration: 1.5, 
                          repeat: Infinity, 
                          ease: "linear" 
                        } 
                      }}
                      className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full"
                    />
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <FolderKanban className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </motion.div>
                  </div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mt-6 text-lg font-medium text-gray-800 dark:text-gray-200"
                  >
                    Görev detayları yükleniyor...
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="flex flex-col items-center mt-3 mb-6 w-64"
                  >
                    <div className="w-full flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                      <span>Görev bilgileri</span>
                      <span>Ekler & notlar</span>
                    </div>
                    <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: "5%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 3, ease: "easeInOut" }}
                        className="h-full bg-primary-500 rounded-full"
                      />
                    </div>
                  </motion.div>
                  
                  <div className="flex flex-col gap-2 items-center">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: [0, 1, 1, 0], x: 0 }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 0.5 }}
                      className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                    >
                      <Clock className="w-4 h-4 text-primary-500" />
                      <span>Görev durumu alınıyor</span>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: [0, 1, 1, 0], x: 0 }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 0.5, delay: 0.5 }}
                      className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                    >
                      <Users className="w-4 h-4 text-blue-500" />
                      <span>Ekip üyeleri yükleniyor</span>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: [0, 1, 1, 0], x: 0 }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 0.5, delay: 1 }}
                      className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                    >
                      <Paperclip className="w-4 h-4 text-green-500" />
                      <span>Dosya ekleri hazırlanıyor</span>
                    </motion.div>
                  </div>
                </div>
              ) : selectedTask ? (
                <>
                  {/* Ana Görev Detayları Bölümü (Sol Taraf) - Orijinal İçerik */}
                  <motion.div 
                    className="flex-1 overflow-y-auto overflow-x-hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    style={{ maxHeight: '85vh' }}
                  >
                    <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-white/70 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-700 backdrop-blur-lg">
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Görev Detayları
                      </h2>
                      <div className="flex items-center gap-2">
                        {currentUser && selectedTask && selectedTask.assigned_members && 
                         selectedTask.assigned_members.some(member => member.id === currentUser.id) && 
                         selectedTask.status !== 'done' && (
                          <button 
                            onClick={async () => {
                              try {
                                const { error } = await supabase
                                  .from('tasks')
                                  .update({ 
                                    status: 'done',
                                    completed_at: new Date().toISOString(),
                                    completed_by: currentUser.id
                                  })
                                  .eq('id', selectedTask.id);

                                if (error) throw error;

                                // Aktivite kaydı oluştur
                                const { error: activityError } = await supabase.from('activities').insert({
                                  user_id: currentUser.id,
                                  activity_type: 'update',
                                  description: `${selectedTask.title} görevini tamamlandı olarak işaretledi.`,
                                  task_id: selectedTask.id
                                });

                                if (activityError) {
                                  console.error('Error creating activity:', activityError);
                                  toast.error('Aktivite kaydı oluşturulurken bir hata oluştu');
                                  return;
                                }

                                // Bildirim gönder
                                await notifyTaskCompleted(
                                  selectedTask,
                                  currentUser.id,
                                  `${currentUser.first_name} ${currentUser.last_name}`
                                );

                                toast.success('Görev başarıyla tamamlandı olarak işaretlendi');
                                fetchTaskDetails(selectedTask.id);
                                fetchTasks();
                              } catch (error) {
                                console.error('Error completing task:', error);
                                toast.error('Görev tamamlanırken bir hata oluştu');
                              }
                            }}
                            className="p-2 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg transition-colors flex items-center gap-1"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-sm font-medium">Tamamlandı olarak işaretle</span>
                          </button>
                        )}
                        {selectedTask && checkPermission(selectedTask, 'edit') && (
                          <button
                            onClick={() => openEditModal(selectedTask)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          </button>
                        )}
                        {selectedTask && checkPermission(selectedTask, 'delete') && (
                          <button
                            onClick={() => setIsDeleteModalOpen(true)}
                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-5 h-5 text-red-500 dark:text-red-400" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="space-y-8">
                        {/* Görev Başlığı ve Durum */}
                        <div className="flex items-start justify-between gap-4 border-b border-gray-200 dark:border-gray-700 pb-4">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-8">
                              {selectedTask.title}
                            </h3>
                            {/* Görevi Oluşturan Kişi Bilgisi */}
                            {selectedTask.created_by_user && (
                              <div className="flex items-center gap-2 mb-4">
                                  <img
                                    src={selectedTask.created_by_user.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedTask.created_by_user.first_name + ' ' + selectedTask.created_by_user.last_name)}&background=3b82f6&color=fff`}
                                    alt={`${selectedTask.created_by_user.first_name} ${selectedTask.created_by_user.last_name}`}
                                    className="w-6 h-6 rounded-full"
                                  />
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  Oluşturan: {selectedTask.created_by_user.first_name} {selectedTask.created_by_user.last_name}
                                  {currentUser && currentUser.id === selectedTask.created_by && (
                                    <span className="ml-1 text-green-600 dark:text-red-400">(Siz)</span>
                                  )}
                                  <span className="text-gray-400 dark:text-gray-500 ml-2">
                                    | {selectedTask.created_by_user.job_position}
                                  </span>
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 text-sm font-medium rounded-full bg-${getStatusColor(selectedTask.status)}-100 dark:bg-${getStatusColor(selectedTask.status)}-900/30 text-${getStatusColor(selectedTask.status)}-700 dark:text-${getStatusColor(selectedTask.status)}-400`}>
                                {TASK_STATUS.find(s => s.id === selectedTask.status)?.label}
                              </span>
                              <span className={`px-3 py-1 text-sm font-medium rounded-full bg-${getPriorityColor(selectedTask.priority)}-100 dark:bg-${getPriorityColor(selectedTask.priority)}-900/30 text-${getPriorityColor(selectedTask.priority)}-700 dark:text-${getPriorityColor(selectedTask.priority)}-400`}>
                                {TASK_PRIORITY.find(p => p.id === selectedTask.priority)?.label} Öncelik
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Görev Tamamlama Bilgisi */}
                        {selectedTask.status === 'done' && (
                          <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <div className="flex items-center gap-3">
                              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                              <div>
                                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                                  Bu görev tamamlandı
                                </p>
                                {selectedTask.completed_by && selectedTask.completed_at && selectedTask.completed_by_user && (
                                  <>
                                    <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                                      Tamamlayan Kişi: {selectedTask.completed_by_user?.first_name} {selectedTask.completed_by_user?.last_name}
                                    </p>
                                    <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                                      {formatDistanceToNow(new Date(selectedTask.completed_at), { addSuffix: true, locale: tr })} tamamlandı
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Proje ve Kategori Bilgileri */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {selectedTask.project?.is_consulting ? 'Danışmanlık Bilgileri' : 'Proje Bilgileri'}
                            </h4>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                {selectedTask.project?.is_consulting ? (
                                  <Building2 className="w-5 h-5 text-gray-400" />
                                ) : (
                                  <FolderKanban className="w-5 h-5 text-gray-400" />
                                )}
                                <span className={`text-gray-900 dark:text-white px-2 py-1 rounded-full text-sm ${
                                  selectedTask.project?.is_consulting
                                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                                    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                }`}>
                                  {selectedTask.project?.name}
                                </span>
                              </div>
                              {selectedTask.sub_project && (
                                <div className="flex items-center gap-2 pt-2">
                                  <ChevronDown className="w-5 h-5 text-gray-400" />
                                  <span className={`text-gray-900 dark:text-white px-2 py-1 rounded-[12px] text-sm ${
                                    selectedTask.project?.is_consulting
                                      ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300'
                                      : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
                                  }`}>
                                    {selectedTask.sub_project.name}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          {!selectedTask.project?.is_consulting && selectedTask.categories && selectedTask.categories.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Kategori & Kategoriler
                              </h4>
                              <div className="flex items-center gap-2">
                                <Tags className="w-5 h-5 text-gray-400" />
                                <div className="flex flex-wrap gap-2">
                                  {selectedTask.categories.map((category) => (
                                    <span
                                      key={category.id}
                                      className={`px-2 py-1 text-sm font-medium rounded-full bg-${category.color}-100 dark:bg-${category.color}-900/30 text-${category.color}-700 dark:text-${category.color}-400`}
                                    >
                                      {category.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Görev Açıklaması */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Görev Açıklaması
                          </h4>
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {selectedTask.description || 'Açıklama bulunmuyor'}
                            </p>
                          </div>
                        </div>

                        {/* Tarih ve İlerleme */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Tarih Bilgileri
                            </h4>
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Calendar className="w-4 h-4" />
                                <span>Başlangıç: {new Date(selectedTask.start_date).toLocaleDateString('tr-TR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Calendar className="w-4 h-4" />
                                <span>Bitiş: {new Date(selectedTask.end_date).toLocaleDateString('tr-TR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}</span>
                              </div>
                              
                              {/* Oluşturulma ve Güncellenme Tarihleri */}
                              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                    <Clock className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    <span>
                                      <span className="font-medium text-green-700 dark:text-green-400">Oluşturulma:</span> {formatDetailedDate(selectedTask.created_at)}
                                    </span>
                                  </div>
                                  {selectedTask.updated_at && selectedTask.updated_at !== selectedTask.created_at && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                      <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                      <span>
                                        <span className="font-medium text-blue-700 dark:text-blue-400">Son Güncelleme:</span> {formatDetailedDate(selectedTask.updated_at)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Süre Bilgileri */}
                              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Proje Süresi</div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {Math.ceil((new Date(selectedTask.end_date) - new Date(selectedTask.start_date)) / (1000 * 60 * 60 * 24))} gün
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Kalan Süre</div>
                                    {(() => {
                                      const now = new Date();
                                      const endDate = new Date(selectedTask.end_date);
                                      const createdDate = new Date(selectedTask.created_at);
                                      
                                      // Bitiş tarihinin saatini görev oluşturulma saati olarak ayarla
                                      endDate.setHours(createdDate.getHours());
                                      endDate.setMinutes(createdDate.getMinutes());
                                      endDate.setSeconds(createdDate.getSeconds());
                                      endDate.setMilliseconds(createdDate.getMilliseconds());
                                      
                                      const timeDiff = endDate - now;
                                      
                                      if (timeDiff <= 0) {
                                        const overdueDays = Math.floor(Math.abs(timeDiff) / (1000 * 60 * 60 * 24));
                                        const overdueHours = Math.floor((Math.abs(timeDiff) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                                        return (
                                          <div className="text-sm font-medium text-red-600 dark:text-red-400">
                                            {overdueDays > 0 ? `${overdueDays}g ${overdueHours}s gecikti!` : `${overdueHours}s gecikti!`}
                                          </div>
                                        );
                                      }

                                      const daysRemaining = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                                      const hoursRemaining = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                                      const minutesRemaining = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                                      
                                      let statusColor = 'text-red-600 dark:text-red-400';
                                      if (daysRemaining > 5) {
                                        statusColor = 'text-green-600 dark:text-green-400';
                                      } else if (daysRemaining > 2) {
                                        statusColor = 'text-yellow-600 dark:text-yellow-400';
                                      } else if (daysRemaining > 0) {
                                        statusColor = 'text-orange-600 dark:text-orange-400';
                                      }

                                      return (
                                        <div className={`text-sm font-medium ${statusColor}`}>
                                          {daysRemaining > 0 
                                            ? `${daysRemaining}g ${hoursRemaining}s`
                                            : hoursRemaining > 0 
                                              ? `${hoursRemaining}s ${minutesRemaining}dk`
                                              : `${minutesRemaining}dk`
                                          }
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Geçen Süre</div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {(() => {
                                        const now = new Date();
                                        const createdDate = new Date(selectedTask.created_at);
                                        const timeDiff = now - createdDate;
                                        
                                        if (timeDiff <= 0) {
                                          return (
                                            <span className="text-blue-600 dark:text-blue-400">
                                              Henüz oluşturulmadı!
                                            </span>
                                          );
                                        }

                                        const daysElapsed = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                                        const hoursElapsed = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                                        const minutesElapsed = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                                        
                                        if (daysElapsed > 0) {
                                          return `${daysElapsed}g ${hoursElapsed}s`;
                                        } else if (hoursElapsed > 0) {
                                          return `${hoursElapsed}s ${minutesElapsed}dk`;
                                        } else {
                                          return `${minutesElapsed}dk`;
                                        }
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          {selectedTask.progress > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                İlerleme Durumu
                              </h4>
                              <div>
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="text-gray-600 dark:text-gray-400">Tamamlanma</span>
                                  <span className="font-medium text-gray-900 dark:text-white">{selectedTask.progress}%</span>
                                </div>
                                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${selectedTask.progress}%` }}
                                    transition={{ duration: 0.5, ease: "easeOut" }}
                                    className="h-full bg-primary-600 rounded-full"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Ekip Üyeleri */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Görevli Ekip Üyeleri
                          </h4>
                          {selectedTask.assigned_members && selectedTask.assigned_members.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {selectedTask.assigned_members.map((member, index) => (
                                <motion.div
                                  key={member.id}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => fetchMemberDetails(member.id)}
                                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                >
                                  <img
                                    src={member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=3b82f6&color=fff`}
                                    alt={member.name}
                                    className="w-6 h-6 rounded-full"
                                  />
                                  <span className="text-sm text-gray-900 dark:text-white">
                                    {member.name}
                                  </span>
                                </motion.div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600/50">
                              <Users className="w-5 h-5 text-gray-400" />
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                Henüz görevli ekip üyesi atanmamış
                              </span>
                              {currentUser && currentUser.id === selectedTask.created_by && (
                                <button 
                                  className="ml-auto text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                                  onClick={() => {
                                    setIsAssignMemberModalOpen(true);
                                    fetchAvailableMembers();
                                    setSelectedMembers(selectedTask.assigned_members || []);
                                  }}
                                >
                                  Ekip Üyesi Ata
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Notlar */}
                        {selectedTask.notes && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Notlar
                            </h4>
                            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {selectedTask.notes}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Ekler */}
                        {selectedTask.attachments && selectedTask.attachments.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Ekler
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {selectedTask.attachments.map((file, index) => {
                                // Determine file type and set appropriate icon
                                const fileType = file.type || file.name.split('.').pop().toLowerCase();
                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].some(ext => 
                                  fileType.includes(ext) || file.name.toLowerCase().endsWith(`.${ext}`)
                                );
                                const isPdf = fileType.includes('pdf') || file.name.toLowerCase().endsWith('.pdf');
                                const isDoc = ['doc', 'docx', 'word'].some(ext => 
                                  fileType.includes(ext) || file.name.toLowerCase().endsWith(`.${ext}`)
                                );
                                const isSpreadsheet = ['xls', 'xlsx', 'sheet', 'csv'].some(ext => 
                                  fileType.includes(ext) || file.name.toLowerCase().endsWith(`.${ext}`)
                                );
                                
                                return (
                                  <motion.div
                                  key={index}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="group relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all"
                                  >
                                    {/* Preview area */}
                                    <div className={`h-32 ${!isImage ? 'flex items-center justify-center bg-gray-50 dark:bg-gray-750' : ''}`}>
                                      {isImage ? (
                                        <img 
                                          src={file.url} 
                                          alt={file.name} 
                                          className="w-full h-full object-cover" 
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMjJDNi40NzcgMjIgMiAxNy41MjMgMiAxMlM2LjQ3NyAyIDEyIDJzMTAgNC40NzcgMTAgMTAtNC40NzcgMTAtMTAgMTB6bTAtMmE4IDggMCAxIDAgMC0xNiA4IDggMCAwIDAgMCAxNnptMC0xMWExIDEgMCAwIDEgMSAxdjVhMSAxIDAgMSAxLTIgMHYtNWExIDEgMCAwIDEgMS0xem0wLTNhMSAxIDAgMSAxIDAgMiAxIDEgMCAwIDEgMC0yeiIgZmlsbD0iY3VycmVudENvbG9yIi8+PC9zdmc+';
                                            e.target.parentNode.classList.add('flex', 'items-center', 'justify-center', 'bg-gray-50', 'dark:bg-gray-750');
                                          }}
                                        />
                                      ) : isPdf ? (
                                        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
                                          <svg className="w-12 h-12 text-red-500 dark:text-red-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                  </div>
                                      ) : isDoc ? (
                                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                          <svg className="w-12 h-12 text-blue-500 dark:text-blue-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      ) : isSpreadsheet ? (
                                        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                                          <svg className="w-12 h-12 text-green-500 dark:text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      ) : (
                                        <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-xl">
                                          <svg className="w-12 h-12 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* File information */}
                                    <div className="p-3">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={file.name}>
                                      {file.name}
                                    </p>
                                      <div className="flex items-center justify-between mt-1">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                                          isImage ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                          isPdf ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                          isDoc ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                          isSpreadsheet ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                          'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                        }`}>
                                          {isImage ? 'Resim' :
                                           isPdf ? 'PDF' :
                                           isDoc ? 'Doküman' :
                                           isSpreadsheet ? 'Tablo' :
                                           'Dosya'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Overlay and download button */}
                                    <a
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 dark:group-hover:bg-black/40 transition-all"
                                    >
                                      <div className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all">
                                        <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2ZM12 20C7.589 20 4 16.411 4 12C4 7.589 7.589 4 12 4C16.411 4 20 7.589 20 12C20 16.411 16.411 20 12 20ZM12 9L16 13H13V17H11V13H8L12 9Z" fill="currentColor" />
                                        </svg>
                                  </div>
                                </a>
                                  </motion.div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  
                  {/* Görev Notları ve Raporlar Paneli (Sağ Taraf) */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                    className="overflow-hidden"
                  >
                    <TaskNotes task={selectedTask} />
                  </motion.div>
                </>
              ) : (
                // Fallback content if no task is selected but modal is open
                <div className="flex items-center justify-center p-12 w-full">
                  <p className="text-gray-500 dark:text-gray-400">Görev bulunamadı.</p>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Üye Detay Modal */}
      <AnimatePresence>
        {isMemberModalOpen && selectedMember && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsMemberModalOpen(false);
                setSelectedMember(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
            >
              <div className="p-6">
              
                <div className="flex items-start justify-between mb-6">
                  
                  <div className="flex items-center gap-4">
                    <img
                      src={selectedMember.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedMember.first_name + ' ' + selectedMember.last_name)}&background=3b82f6&color=fff`}
                      alt={`${selectedMember.first_name} ${selectedMember.last_name}`}
                      className="w-16 h-16 rounded-full border-4 border-white dark:border-gray-700 shadow-lg"
                    />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {selectedMember.first_name} {selectedMember.last_name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedMember.job_position || 'Pozisyon belirtilmemiş'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsMemberModalOpen(false);
                      setSelectedMember(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white m-4">
                  Personel Bilgileri
                </h2>
                
                  {/* İletişim Bilgileri */}
                  <div>
                    
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      İletişim Bilgileri
                    </h4>
                    <div className="space-y-2">
                      {selectedMember.email && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <Mail className="w-4 h-4" />
                          <span>{selectedMember.email}</span>
                        </div>
                      )}
                      {selectedMember.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <Phone className="w-4 h-4" />
                          <span>{selectedMember.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Departman Bilgisi */}
                  {selectedMember.department && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Departman
                      </h4>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900 dark:text-white">
                          {selectedMember.department}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Durum */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Durum
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        selectedMember.online_status ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      <span className="text-sm text-gray-900 dark:text-white">
                        {selectedMember.online_status ? 'Çevrimiçi' : 'Çevrimdışı'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Ekip Üyesi Atama Modal */}
      <AnimatePresence>
        {isAssignMemberModalOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsAssignMemberModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-xl"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Ekip Üyesi Ata
                  </h3>
                  <button
                    onClick={() => setIsAssignMemberModalOpen(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                {/* Arama */}
                <div className="relative mb-4">
                  <input
                    type="text"
                    placeholder="Kullanıcı ara..."
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>

                {/* Kullanıcı Listesi */}
                <div className="max-h-[400px] overflow-y-auto">
                  <div className="space-y-2">
                    {getFilteredMembers().map((member) => {
                      const isSelected = selectedMembers.some(m => m.id === member.id);
                      return (
                        <div
                          key={member.id}
                          onClick={() => toggleMemberSelection(member)}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-500 dark:border-primary-400'
                              : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600/50'
                          }`}
                        >
                          <div className="flex-shrink-0">
                            <img
                              src={member.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.first_name + ' ' + member.last_name)}&background=3b82f6&color=fff`}
                              alt={`${member.first_name} ${member.last_name}`}
                              className="w-10 h-10 rounded-full"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {member.first_name} {member.last_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {member.job_position || 'Pozisyon belirtilmemiş'}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            {isSelected && (
                              <div className="w-6 h-6 bg-primary-500 dark:bg-primary-400 rounded-full flex items-center justify-center">
                                <CheckCircle2 className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Alt Kısım */}
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedMembers.length} kişi seçildi
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsAssignMemberModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={assignMembersToTask}
                      disabled={isAssigning}
                      className={`px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg transition-colors ${
                        isAssigning ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isAssigning ? 'Atanıyor...' : 'Ekip Üyelerini Ata'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Task Modal */}
      <AnimatePresence>
        {isEditModalOpen && selectedTask && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsEditModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-4xl max-h-[85vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-xl"
              style={{ overflowY: 'auto', maxHeight: '85vh' }}
            >
              <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-white/30 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-700 backdrop-blur-xl">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Görevi Düzenle
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 dark:rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Vazgeç
                  </button>
                  <button
                    onClick={handleUpdateTask} 
                    disabled={isEditing}
                    className={`px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 ${
                      isEditing ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isEditing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Güncelleniyor...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Değişiklikleri Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="p-6">
                <form className="space-y-8" onSubmit={handleUpdateTask}>
                  {/* Proje ve Kategori Seçimi */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Projeler
                      </label>
                      <div className="relative">
                        <select
                          name="project_id"
                          value={editFormData.project_id}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleEditInputChange(e);
                            if (value === 'consulting') {
                              setEditFormData(prev => ({
                                ...prev,
                                project_id: value,
                                sub_project_id: '',
                                category_id: [],
                                consulting_id: '',
                                consulting_project_id: ''
                              }));
                              setEditSelectedCategories([]);
                            } else if (value) {
                              fetchSubProjects(value);
                              setEditFormData(prev => ({
                                ...prev,
                                project_id: value,
                                sub_project_id: '',
                                consulting_id: null,
                                consulting_project_id: null
                              }));
                            }
                          }}
                          className={`w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border ${
                            editErrors.project_id
                              ? 'border-red-500 focus:ring-red-500'
                              : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                          } rounded-lg text-gray-900 dark:text-white appearance-none focus:ring-2 focus:border-transparent transition-colors`}
                        >
                          <option value="">Proje Seçin</option>
                          <option value="consulting">Danışmanlık</option>
                          {projects.filter(p => !p.is_consulting && p.id !== 'all').map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                        <FolderKanban className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      </div>
                      {editErrors.project_id && (
                        <p className="mt-1 text-sm text-red-500">{editErrors.project_id}</p>
                      )}
                    </div>

                    {/* Danışmanlık seçiliyse firma ve proje seçimi göster */}
                    {editFormData.project_id === 'consulting' && (
                      <>
                        {/* Firma Seçimi */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Danışmanlık Firması
                          </label>
                          <div className="relative">
                            <select
                              name="consulting_id"
                              value={editFormData.consulting_id}
                              onChange={(e) => {
                                handleEditInputChange(e);
                                if (e.target.value) {
                                  fetchConsultingProjects(e.target.value);
                                }
                                setEditFormData(prev => ({
                                  ...prev,
                                  consulting_id: e.target.value,
                                  consulting_project_id: ''
                                }));
                              }}
                              className={`w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border ${
                                editErrors.consulting_id
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                              } rounded-lg text-gray-900 dark:text-white appearance-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors`}
                            >
                              <option value="">Firma Seçin</option>
                              {consultingFirms.map((firm) => (
                                <option key={firm.id} value={firm.id}>
                                  {firm.name}
                                </option>
                              ))}
                            </select>
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          </div>
                          {editErrors.consulting_id && (
                            <p className="mt-1 text-sm text-red-500">{editErrors.consulting_id}</p>
                          )}
                        </div>

                        {/* Firma Projesi Seçimi */}
                        {editFormData.consulting_id && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Firma Projesi
                            </label>
                            <div className="relative">
                              <select
                                name="consulting_project_id"
                                value={editFormData.consulting_project_id}
                                onChange={handleEditInputChange}
                                className={`w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border ${
                                  editErrors.consulting_project_id
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                                } rounded-lg text-gray-900 dark:text-white appearance-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors`}
                              >
                                <option value="">Proje Seçin</option>
                                {consultingProjects[editFormData.consulting_id]?.map((project) => (
                                  <option key={project.id} value={project.id}>
                                    {project.name}
                                  </option>
                                ))}
                              </select>
                              <GitBranch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            </div>
                            {editErrors.consulting_project_id && (
                              <p className="mt-1 text-sm text-red-500">{editErrors.consulting_project_id}</p>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Alt Proje ve Kategori seçimleri sadece özel proje tipi değilse ve danışmanlık değilse göster */}
                    {!isSpecialProjectType(editFormData.project_id) && editFormData.project_id && editFormData.project_id !== 'consulting' && (
                      <>
                        {/* Alt Proje Seçimi */}
                        {subProjects[editFormData.project_id]?.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {projects.find(p => p.id === editFormData.project_id)?.name === 'Bakım' ? 'Alt Başlık' : 'Alt Projeler'}
                            </label>
                            <div className="relative">
                              <select
                                name="sub_project_id"
                                value={editFormData.sub_project_id}
                                onChange={handleEditInputChange}
                                className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white appearance-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                              >
                                {/* Bakım projeleri için alt başlık seçimi 
                                <option value="">{projects.find(p => p.id === editFormData.project_id)?.name === 'Bakım' ? 'Alt Başlık Seçin' : 'Alt Proje Seçin'}</option>
                                
                                */}
                                {subProjects[editFormData.project_id].map((subProject) => (
                                  <option key={subProject.id} value={subProject.id}>
                                    {subProject.name}
                                  </option>
                                ))}
                              </select>
                              <GitBranch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            </div>
                          </div>
                        )}

                        {/* İş Paketleri - Sadece Bakım projesi seçili değilse göster */}
                        {false && projects.find(p => p.id === editFormData.project_id)?.name !== 'Bakım' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              İş Paketleri
                            </label>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                                className={`w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border ${
                                  editErrors.category_id
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                                } rounded-lg text-left focus:outline-none focus:ring-2 focus:border-transparent transition-colors`}
                              >
                                <div className="flex items-center gap-2">
                                  <Tags className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                  {editSelectedCategories.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {editSelectedCategories.map(category => (
                                        <span
                                          key={category.id}
                                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${category.color || 'gray'}-100 text-${category.color || 'gray'}-700 dark:bg-${category.color || 'gray'}-900/30 dark:text-${category.color || 'gray'}-400`}
                                        >
                                          {category.name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500 dark:text-gray-400">Kategorileri Seçin</span>
                                  )}
                                </div>
                              </button>
                              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

                              {/* Dropdown Menu */}
                              <AnimatePresence>
                                {isCategoryDropdownOpen && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
                                  >
                                    <div className="p-2 max-h-60 overflow-y-auto">
                                      {categories.map(category => (
                                        <button
                                          key={category.id}
                                          type="button"
                                          onClick={() => handleEditCategoryToggle(category)}
                                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                          <div className={`w-4 h-4 rounded-full border-2 ${
                                            editSelectedCategories.some(cat => cat.id === category.id)
                                              ? 'bg-primary-500 border-primary-500'
                                              : 'border-gray-300 dark:border-gray-600'
                                          }`}>
                                            {editSelectedCategories.some(cat => cat.id === category.id) && (
                                              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                              </svg>
                                            )}
                                          </div>
                                          <span className={`text-sm font-medium text-${category.color || 'gray'}-700 dark:text-${category.color || 'gray'}-400`}>
                                            {category.name}
                                          </span>
                                        </button>
                                      ))}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                            {editErrors.category_id && (
                              <p className="mt-1 text-sm text-red-500">{editErrors.category_id}</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Sol Kolon */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Başlık */}
                      <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Görev Başlığı
                        </label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          value={editFormData.title}
                          onChange={handleEditInputChange}
                          className={`w-full px-4 py-2.5 rounded-lg border ${
                            editErrors.title
                              ? 'border-red-500 focus:ring-red-500'
                              : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                          } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent transition-colors`}
                          placeholder="Kısa ve açıklayıcı bir başlık girin"
                        />
                        {editErrors.title && (
                          <p className="mt-1 text-sm text-red-500">{editErrors.title}</p>
                        )}
                      </div>

                      {/* Açıklama */}
                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Görev Açıklaması
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          value={editFormData.description}
                          onChange={handleEditInputChange}
                          rows={6}
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                          placeholder="Görev detaylarını açıklayın..."
                        />
                      </div>
                    </div>

                    {/* Sağ Kolon */}
                    <div className="space-y-6">
                      {/* Öncelik */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Öncelik
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            type="button"
                            onClick={() => setEditFormData(prev => ({ ...prev, priority: 'low' }))}
                            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${
                              editFormData.priority === 'low'
                                ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
                                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                            } transition-colors`}
                          >
                            <span className="text-sm font-medium">Düşük</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditFormData(prev => ({ ...prev, priority: 'medium' }))}
                            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${
                              editFormData.priority === 'medium'
                                ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400'
                                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                            } transition-colors`}
                          >
                            <span className="text-sm font-medium">Orta</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditFormData(prev => ({ ...prev, priority: 'high' }))}
                            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${
                              editFormData.priority === 'high'
                                ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
                                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                            } transition-colors`}
                          >
                            <span className="text-sm font-medium">Yüksek</span>
                          </button>
                        </div>
                      </div>

                      {/* Tarihler */}
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Başlangıç Tarihi
                          </label>
                          <div className="relative">
                            <input
                              type="date"
                              id="start_date"
                              name="start_date"
                              value={editFormData.start_date}
                              onChange={handleEditInputChange}
                              max={editFormData.end_date || undefined}
                              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                                editErrors.start_date
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                              } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent transition-colors`}
                            />
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          </div>
                          {editErrors.start_date && (
                            <p className="mt-1 text-sm text-red-500">{editErrors.start_date}</p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Bitiş Tarihi
                          </label>
                          <div className="relative">
                            <input
                              type="date"
                              id="end_date"
                              name="end_date"
                              value={editFormData.end_date}
                              onChange={handleEditInputChange}
                              min={editFormData.start_date || undefined}
                              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                                editErrors.end_date
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                              } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:border-transparent transition-colors`}
                            />
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          </div>
                          {editErrors.end_date && (
                            <p className="mt-1 text-sm text-red-500">{editErrors.end_date}</p>
                          )}
                        </div>
                      </div>

                      {/* Notlar */}
                      <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Notlar
                        </label>
                        <textarea
                          id="notes"
                          name="notes"
                          value={editFormData.notes}
                          onChange={handleEditInputChange}
                          rows={4}
                          className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                          placeholder="Ek notlarınızı buraya ekleyin..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Add File Attachments Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ekler
                    </label>
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                        const droppedFiles = Array.from(e.dataTransfer.files);
                        setEditFiles([...editFiles, ...droppedFiles]);
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-300 ${
                        isDragging
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <motion.div
                          animate={{
                            scale: isDragging ? 1.1 : 1,
                            rotate: isDragging ? 180 : 0,
                          }}
                          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                        >
                          <Paperclip
                            className={`w-8 h-8 ${
                              isDragging ? 'text-primary-500' : 'text-gray-400'
                            } transition-colors`}
                          />
                        </motion.div>

                        <motion.div
                          animate={{
                            scale: isDragging ? 1.05 : 1,
                          }}
                          className="text-center mt-2"
                        >
                          <p
                            className={`text-sm ${
                              isDragging
                                ? 'text-primary-600 dark:text-primary-400'
                                : 'text-gray-500 dark:text-gray-400'
                            } transition-colors mb-1`}
                          >
                            {isDragging ? (
                              <span className="font-medium">Dosyaları buraya bırakın</span>
                            ) : (
                              <span>
                                Dosyaları sürükleyip bırakın veya{' '}
                                <span className="text-primary-600 dark:text-primary-400">
                                  seçin
                                </span>
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            PDF, DOCX, XLSX, JPG, PNG (max. 2GB)
                          </p>
                        </motion.div>

                        <input
                          type="file"
                          multiple
                          onChange={handleEditFileChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                    </div>

                    {/* Display selected files */}
                    {editFiles.length > 0 && (
                      <div className="mt-4 space-y-6">
                        {/* Existing files section */}
                        {editFiles.filter(file => file.url).length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Mevcut Dosyalar
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                              {editFiles.filter(file => file.url).map((file, index) => {
                                // Determine file type and set appropriate icon
                                const fileType = file.type || file.name.split('.').pop().toLowerCase();
                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].some(ext => 
                                  fileType.includes(ext) || file.name.toLowerCase().endsWith(`.${ext}`)
                                );
                                const isPdf = fileType.includes('pdf') || file.name.toLowerCase().endsWith('.pdf');
                                const isDoc = ['doc', 'docx', 'word'].some(ext => 
                                  fileType.includes(ext) || file.name.toLowerCase().endsWith(`.${ext}`)
                                );
                                const isSpreadsheet = ['xls', 'xlsx', 'sheet', 'csv'].some(ext => 
                                  fileType.includes(ext) || file.name.toLowerCase().endsWith(`.${ext}`)
                                );
                                
                                const realIndex = editFiles.findIndex(f => f === file);
                                
                                return (
                                  <div
                                    key={`existing-${index}`}
                                    className="group relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all"
                                  >
                                    {/* Preview area */}
                                    <div className={`h-24 ${!isImage ? 'flex items-center justify-center bg-gray-50 dark:bg-gray-750' : ''}`}>
                                      {isImage ? (
                                        <img 
                                          src={file.url} 
                                          alt={file.name} 
                                          className="w-full h-full object-cover" 
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMjJDNi40NzcgMjIgMiAxNy41MjMgMiAxMlM2LjQ3NyAyIDEyIDJzMTAgNC40NzcgMTAgMTAtNC40NzcgMTAtMTAgMTB6bTAtMmE4IDggMCAxIDAgMC0xNiA4IDggMCAwIDAgMCAxNnptMC0xMWExIDEgMCAwIDEgMSAxdjVhMSAxIDAgMSAxLTIgMHYtNWExIDEgMCAwIDEgMS0xem0wLTNhMSAxIDAgMSAxIDAgMiAxIDEgMCAwIDEgMC0yeiIgZmlsbD0iY3VycmVudENvbG9yIi8+PC9zdmc+';
                                            e.target.parentNode.classList.add('flex', 'items-center', 'justify-center', 'bg-gray-50', 'dark:bg-gray-750');
                                          }}
                                        />
                                      ) : isPdf ? (
                                        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
                                          <svg className="w-8 h-8 text-red-500 dark:text-red-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                              </div>
                                      ) : isDoc ? (
                                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                          <svg className="w-8 h-8 text-blue-500 dark:text-blue-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      ) : isSpreadsheet ? (
                                        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                                          <svg className="w-8 h-8 text-green-500 dark:text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      ) : (
                                        <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-xl">
                                          <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* File information */}
                                    <div className="p-2">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={file.name}>
                                      {file.name}
                                    </p>
                                      <div className="flex items-center justify-between mt-1">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                                          isImage ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                          isPdf ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                          isDoc ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                          isSpreadsheet ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                          'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                        }`}>
                                          {isImage ? 'Resim' :
                                           isPdf ? 'PDF' :
                                           isDoc ? 'Doküman' :
                                           isSpreadsheet ? 'Tablo' :
                                           'Dosya'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Remove button */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeEditFile(realIndex, true);
                                      }}
                                      className="absolute top-1 right-1 p-1.5 bg-white/80 dark:bg-gray-800/80 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* New files to upload section */}
                        {editFiles.filter(file => !file.url).length > 0 && (
                              <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              Yüklenecekler
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                              {editFiles.filter(file => !file.url).map((file, index) => {
                                // Create a preview URL for local files if they're images
                                const previewUrl = file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
                                const fileType = file.type || '';
                                const isImage = fileType.startsWith('image/');
                                const isPdf = fileType === 'application/pdf';
                                const isDoc = ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(fileType);
                                const isSpreadsheet = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(fileType);
                                
                                const realIndex = editFiles.findIndex(f => f === file);
                                
                                return (
                                  <div
                                    key={`new-${index}`}
                                    className="group relative rounded-xl overflow-hidden border-2 border-green-200 dark:border-green-800 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all"
                                  >
                                    <div className="absolute top-0 left-0 p-1 px-2 text-xs font-medium text-white bg-green-500 rounded-br-lg">
                                      Yeni
                                    </div>
                                    
                                    {/* Preview area */}
                                    <div className={`h-24 ${!isImage ? 'flex items-center justify-center bg-gray-50 dark:bg-gray-750' : ''}`}>
                                      {isImage && previewUrl ? (
                                        <img 
                                          src={previewUrl} 
                                          alt={file.name} 
                                          className="w-full h-full object-cover"
                                        />
                                      ) : isPdf ? (
                                        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
                                          <svg className="w-8 h-8 text-red-500 dark:text-red-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      ) : isDoc ? (
                                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                          <svg className="w-8 h-8 text-blue-500 dark:text-blue-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      ) : isSpreadsheet ? (
                                        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                                          <svg className="w-8 h-8 text-green-500 dark:text-green-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      ) : (
                                        <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-xl">
                                          <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
                                            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
                                            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* File information */}
                                    <div className="p-2">
                                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate" title={file.name}>
                                  {file.name}
                                </p>
                                      <div className="flex items-center justify-between mt-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                          {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                          Yüklenecek
                                        </span>
                              </div>
                            </div>
                                    
                                    {/* Remove button */}
                                    <button
                              type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeEditFile(realIndex, false);
                                      }}
                                      className="absolute top-1 right-1 p-1.5 bg-white/80 dark:bg-gray-800/80 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Files to be deleted section - To be implemented once we have a separate structure for tracking deletions */}
                        {/* For now, we just remove files directly from editFiles state, but we could add a "markedForDeletion" state */}
                        
                        <div className="flex justify-end pt-2">
                          <button
                            type="button"
                            onClick={() => setEditFiles([])}
                            className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1.5 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Tümünü Temizle
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Add Team Members Section - IMPROVED */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ekip Üyeleri
                    </label>
                    
                    {/* Already Selected Members Display */}
                    {tempSelectedMembers.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                            <Users className="w-4 h-4" /> 
                            Seçilen Üyeler ({tempSelectedMembers.length})
                          </h4>
                          <button
                            type="button"
                            onClick={() => setTempSelectedMembers([])}
                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                            Tümünü Kaldır
                          </button>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                          {tempSelectedMembers.map(member => (
                            <div 
                              key={`selected-${member.id}`}
                              className="flex items-center gap-2 pl-2 pr-1 py-1 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm"
                            >
                              <img
                                src={member.profile_photo_url || getDefaultAvatar(member.first_name, member.last_name)}
                                alt={`${member.first_name} ${member.last_name}`}
                                className="w-6 h-6 rounded-full"
                              />
                              <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                                {member.first_name} {member.last_name}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTempSelectedMembers(tempSelectedMembers.filter(m => m.id !== member.id));
                                }}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full transition-colors"
                              >
                                <X className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                              </button>
                            </div>
                          ))}
                          {tempSelectedMembers.length === 0 && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 p-2">
                              Henüz üye seçilmedi
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Search and Filter */}
                    <div className="relative mb-3">
                        <input
                          type="text"
                          placeholder="Üye ara..."
                          value={memberSearchQuery}
                          onChange={(e) => setMemberSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        {memberSearchQuery && (
                          <button
                            onClick={() => setMemberSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                          >
                            <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </button>
                        )}
                      </div>

                    {/* Available Members Section */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="bg-gray-50 dark:bg-gray-800/50 px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Kullanılabilir Üyeler</h4>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {memberSearchQuery 
                            ? `${filteredTeamMembers.length} sonuç bulundu` 
                            : `${teamMembers.length} üye`}
                        </div>
                      </div>

                      <div className="max-h-[250px] overflow-y-auto space-y-0.5 divide-y divide-gray-100 dark:divide-gray-700">
                        {filteredTeamMembers.length > 0 ? (
                          filteredTeamMembers.map((member) => {
                            const isSelected = tempSelectedMembers.some(m => m.id === member.id);
                            return (
                              <motion.div
                                key={member.id}
                                initial={false}
                                animate={{
                                  backgroundColor: isSelected ? 'var(--primary-50)' : 'transparent',
                                  borderColor: isSelected ? 'var(--primary-500)' : 'var(--gray-200)',
                                }}
                                onClick={() => {
                                  if (isSelected) {
                                    setTempSelectedMembers(tempSelectedMembers.filter(m => m.id !== member.id));
                                  } else {
                                    setTempSelectedMembers([...tempSelectedMembers, member]);
                                  }
                                }}
                                className={`flex items-center gap-3 p-3 cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                                  isSelected ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 dark:border-primary-400' : ''
                                }`}
                              >
                                <div className="relative">
                                <img
                                  src={member.profile_photo_url || getDefaultAvatar(member.first_name, member.last_name)}
                                  alt={`${member.first_name} ${member.last_name}`}
                                    className={`w-10 h-10 rounded-full ${isSelected ? 'ring-2 ring-primary-500 dark:ring-primary-400' : ''}`}
                                  />
                                  {isSelected && (
                                    <div className="absolute -right-1 -bottom-1 bg-primary-500 dark:bg-primary-400 rounded-full w-5 h-5 flex items-center justify-center">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate flex items-center gap-2">
                                    {member.first_name} {member.last_name}
                                    
                                    {/* Current user indicator */}
                                    {currentUser && member.id === currentUser.id && (
                                      <span className="text-xs font-normal px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                        Siz
                                      </span>
                                    )}
                                  </p>
                                  
                                  <div className="flex items-center gap-2">
                                    {member.job_position && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {member.job_position}
                                      </p>
                                    )}
                                    
                                    {member.department && (
                                      <>
                                        <span className="text-xs text-gray-400">•</span>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                          {member.department}
                                        </p>
                                      </>
                                    )}
                                </div>
                                    </div>
                                
                                <div className="flex-shrink-0">
                                  {isSelected ? (
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setTempSelectedMembers(tempSelectedMembers.filter(m => m.id !== member.id));
                                      }}
                                      className="p-1.5 bg-primary-100 dark:bg-primary-900/30 rounded-full hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                                    >
                                      <X className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setTempSelectedMembers([...tempSelectedMembers, member]);
                                      }}
                                      className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    >
                                      <Plus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </button>
                                  )}
                                </div>
                              </motion.div>
                            );
                          })
                        ) : (
                          <div className="flex flex-col items-center justify-center py-6 text-gray-500 dark:text-gray-400">
                            <Users className="w-8 h-8 mb-2 opacity-40" />
                            <p className="text-sm mb-1">Üye bulunamadı</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">Farklı bir arama terimi deneyin</p>
                          </div>
                        )}
                      </div>
                      </div>

                    {/* Save notification */}
                    <div className="mt-3 flex items-center gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800/30">
                      <Info size={16} className="text-blue-500 flex-shrink-0" />
                      <span className="text-xs text-blue-700 dark:text-blue-300">
                        Üye değişiklikleri sadece "Değişiklikleri Kaydet" butonuna tıklandığında uygulanacaktır.
                          </span>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {isDeleteModalOpen && selectedTask && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center overflow-hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => !isDeleting && setIsDeleteModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-full max-w-md bg-white/30 dark:bg-gray-800/30 border border-gray-300 dark:border-gray-600 rounded-xl shadow-xl p-6 backdrop-blur-lg"
            >
              <div className="text-center">
                <AlertCircle className="w-12 h-12 mx-auto text-red-500 dark:text-red-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Görevi Sil
                </h3>
                <p className="text-sm text-gray-800 dark:text-gray-400 mb-6">
                  Bu görevi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve görevle ilgili tüm veriler (kategoriler, dosyalar vb.) silinecektir.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => !isDeleting && setIsDeleteModalOpen(false)}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    Vazgeç
                  </button>
                  <button
                    onClick={handleDeleteTask}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {isDeleting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Siliniyor...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Görevi Sil
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      
    </div>
  );
};

export default Tasks; 